import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { 
  Users, 
  UserCheck, 
  UserX, 
  User,
  Clock, 
  CheckCircle, 
  XCircle, 
  Briefcase,
  Bug,
  FileText,
  BarChart3,
  ArrowRight,
  AlertCircle,
  RefreshCw,
  Eye,
  Power,
  PowerOff,
  Trash2,
  Search,
  TrendingUp,
  PieChart,
  Activity,
  Target,
  Calendar,
  Building2,
  Trophy,
  Monitor,
  Smartphone,
  Tablet,
  Mouse,
  Scroll,
  Share2,
  Globe,
  ExternalLink,
  Settings,
  Bot,
  Download
} from "lucide-react";
import { dashboardAPI, adminJobAPI, hackathonAPI, adminPaymentAPI } from "../../services/api";
import { useToast } from "../../contexts/ToastContext";
import ToastContainer from "../../components/ui/ToastContainer";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import clarityService, { ClarityMetrics } from "../../services/clarityService";
import RegistrationTrendChart from "../../components/ui/RegistrationTrendChart";
import AspirantInterestsChart from "../../components/ui/AspirantInterestsChart";
import ExperienceDistributionChart from "../../components/ui/ExperienceDistributionChart";

interface User {
  id: string;
  email: string;
  fullName: string;
  role: string;
  registrationNumber?: string;
  phone: string;
  address: string;
  resumeUrl: string;
  bio?: string;
  dateOfBirth?: string;
  tShirtSize?: string;
  profileCompleted?: number;
  lookingForJob?: boolean;
  upskilling?: boolean;
  justCommunityMember?: boolean;
  isEmailVerified: boolean;
  isApproved: boolean;
  isActive: boolean;
  rejectionReason?: string;
  createdAt: string;
  primarySkills?: string[];
  secondarySkills?: string[];
  careerGoals?: {
    dreamCompany?: string;
    targetRole?: string;
    salaryExpectations?: string;
    timeline?: string;
  };
  education?: {
    degree?: string;
    institution?: string;
    fieldOfStudy?: string;
    graduationYear?: number;
    gpa?: string;
  };
  skills?: {
    technical?: string[];
    soft?: string[];
    certifications?: string[];
  };
  experience?: Array<{
    company?: string;
    position?: string;
    startDate?: string;
    endDate?: string;
    isCurrent?: boolean;
    description?: string;
  }>;
  professionalInfo?: {
    currentCompany?: string;
    currentPosition?: string;
    yearsOfExperience?: number;
    industry?: string;
    department?: string;
  };
  achievements?: string[];
}

interface Job {
  _id: string;
  jobId: string;
  title: string;
  companyName: string;
  location: string;
  employmentType: string;
  experienceLevel: string;
  salaryRange?: {
    min: number;
    max: number;
  };
  jobCategory?: string;
  numberOfOpenings?: number;
  description: string;
  requirements: string[];
  responsibilities: string[];
  benefits: string[];
  requiredSkills: string[];
  applicationDeadline: string;
  lastDateToApply?: string;
  contactEmail: string;
  contactPhone: string;
  status: 'pending' | 'approved' | 'rejected';
  isActive: boolean;
  views?: number;
  applications?: number;
  createdAt: string;
  postedBy: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  approvedBy?: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  rejectedBy?: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  rejectionReason?: string;
  updatedAt: string;
}

interface BugReport {
  _id: string;
  bugId: string;
  reportedBy: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  reporterEmail: string;
  reporterName: string;
  module: string;
  issueDescription: string;
  stepsToReproduce?: string;
  expectedResult?: string;
  actualResult?: string;
  browserInfo?: string;
  osInfo?: string;
  additionalInfo?: string;
  priority: string;
  bugType: string;
  status: string;
  createdAt: string;
  updatedAt?: string;
  assignedTo?: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  adminNotes?: string;
}

interface DashboardStats {
  users: {
    total: number;
    pending: number;
    approved: number;
    rejected: number;
  };
  jobs: {
    total: number;
    pending: number;
    approved: number;
    rejected: number;
  };
  tickets: {
    total: number;
    open: number;
    inProgress: number;
    resolved: number;
  };
  hackathons: {
    total: number;
    active: number;
    upcoming: number;
    completed: number;
  };
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [pendingUsers, setPendingUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [showFullProfile, setShowFullProfile] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [approving, setApproving] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [toggling, setToggling] = useState<string | null>(null);
  
  // Job management state
  const [allJobs, setAllJobs] = useState<Job[]>([]);
  const [pendingJobs, setPendingJobs] = useState<Job[]>([]);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [showJobDetails, setShowJobDetails] = useState(false);
  const [showJobRejectDialog, setShowJobRejectDialog] = useState(false);
  const [jobRejectionReason, setJobRejectionReason] = useState('');
  const [jobSearchTerm, setJobSearchTerm] = useState('');
  const [jobStatusFilter, setJobStatusFilter] = useState('all');
  const [jobApproving, setJobApproving] = useState<string | null>(null);
  const [jobRejecting, setJobRejecting] = useState<string | null>(null);
  
  // Ticket management state
  const [allTickets, setAllTickets] = useState<BugReport[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<BugReport | null>(null);
  const [showTicketDetails, setShowTicketDetails] = useState(false);
  const [ticketSearchTerm, setTicketSearchTerm] = useState('');
  const [ticketStatusFilter, setTicketStatusFilter] = useState('all');
  const [ticketPriorityFilter, setTicketPriorityFilter] = useState('all');
  const [ticketLoading, setTicketLoading] = useState(false);
  
  // Analytics state
  const [analyticsTab, setAnalyticsTab] = useState('site');
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [registrationTrendData, setRegistrationTrendData] = useState<any[]>([]);
  const [aspirantInterestsData, setAspirantInterestsData] = useState<any[]>([]);
  const [experienceData, setExperienceData] = useState<any[]>([]);
  const [industryData, setIndustryData] = useState<any[]>([]);
  const [placedAspirantsData, setPlacedAspirantsData] = useState<any[]>([]);
  const [trendPeriod, setTrendPeriod] = useState('month');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  
  // Clarity Analytics State
  const [clarityMetrics, setClarityMetrics] = useState<ClarityMetrics | null>(null);
  const [clarityLoading, setClarityLoading] = useState(false);
  
  const { toasts, success, error, removeToast } = useToast();

  // Dialog states for confirmations
  const [showDeleteTicketDialog, setShowDeleteTicketDialog] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState<string | null>(null);
  const [showDeleteUserDialog, setShowDeleteUserDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const [showDeleteJobDialog, setShowDeleteJobDialog] = useState(false);
  const [jobToDelete, setJobToDelete] = useState<any | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20); // Items per page for all entities
  
  // User pagination state
  const [userPage, setUserPage] = useState(1);
  const [userTotalPages, setUserTotalPages] = useState(1);
  const [userTotal, setUserTotal] = useState(0);
  
  // Job pagination state  
  const [jobPage, setJobPage] = useState(1);
  const [jobTotalPages, setJobTotalPages] = useState(1);
  const [jobTotal, setJobTotal] = useState(0);
  
  // Ticket pagination state
  const [ticketPage, setTicketPage] = useState(1);
  const [ticketTotalPages, setTicketTotalPages] = useState(1);
  const [ticketTotal, setTicketTotal] = useState(0);

  // Payment management state
  const [hackathons, setHackathons] = useState<any[]>([]);
  const [selectedHackathon, setSelectedHackathon] = useState<string>('');
  const [hackathonPayments, setHackathonPayments] = useState<any[]>([]);
  const [hackathonRegistrations, setHackathonRegistrations] = useState<any[]>([]);
  const [paymentStats, setPaymentStats] = useState<any>(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState<{
    userId: string;
    action: 'approve' | 'revert';
    hackathonId: string;
    userName: string;
    hackathonTitle: string;
  } | null>(null);
  const [processingPayment, setProcessingPayment] = useState<string | null>(null);
  const [isPageDisabled, setIsPageDisabled] = useState(false);

  // Excel export functions
  const exportUsersToExcel = () => {
    const exportData = filteredUsers.map(user => ({
      'Full Name': user.fullName,
      'Email': user.email,
      'Role': user.role,
      'Phone': user.phone || '',
      'Address': user.address || '',
      'Registration Number': user.registrationNumber || '',
      'Status': user.isApproved ? 'Approved' : user.rejectionReason ? 'Rejected' : 'Pending',
      'Account Status': user.isActive ? 'Active' : 'Inactive',
      'Email Verified': user.isEmailVerified ? 'Yes' : 'No',
      'Joined Date': new Date(user.createdAt).toLocaleDateString(),
      'Date of Birth': user.dateOfBirth ? new Date(user.dateOfBirth).toLocaleDateString() : '',
      'T-Shirt Size': user.tShirtSize || '',
      'Bio': user.bio || '',
      'Profile Completion': `${user.profileCompleted || 0}%`,
      'Looking for Job': user.lookingForJob ? 'Yes' : 'No',
      'Upskilling': user.upskilling ? 'Yes' : 'No',
      'Just Community Member': user.justCommunityMember ? 'Yes' : 'No',
      'Primary Skills': user.primarySkills?.join(', ') || '',
      'Secondary Skills': user.secondarySkills?.join(', ') || '',
      'Technical Skills': user.skills?.technical?.join(', ') || '',
      'Soft Skills': user.skills?.soft?.join(', ') || '',
      'Certifications': user.skills?.certifications?.join(', ') || '',
      'Dream Company': user.careerGoals?.dreamCompany || '',
      'Target Role': user.careerGoals?.targetRole || '',
      'Salary Expectations': user.careerGoals?.salaryExpectations || '',
      'Timeline': user.careerGoals?.timeline || '',
      'Current Company': user.professionalInfo?.currentCompany || '',
      'Current Position': user.professionalInfo?.currentPosition || '',
      'Years of Experience': user.professionalInfo?.yearsOfExperience || '',
      'Industry': user.professionalInfo?.industry || '',
      'Department': user.professionalInfo?.department || '',
      'Degree': user.education?.degree || '',
      'Institution': user.education?.institution || '',
      'Field of Study': user.education?.fieldOfStudy || '',
      'Graduation Year': user.education?.graduationYear || '',
      'GPA': user.education?.gpa || '',
      'Experience': user.experience?.map(exp => 
        `${exp.company || 'N/A'} - ${exp.position || 'N/A'} (${exp.startDate ? new Date(exp.startDate).toLocaleDateString() : 'N/A'} to ${exp.isCurrent ? 'Present' : exp.endDate ? new Date(exp.endDate).toLocaleDateString() : 'N/A'})`
      ).join('; ') || '',
      'Achievements': user.achievements?.join(', ') || '',
      'Rejection Reason': user.rejectionReason || ''
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Users');
    
    // Auto-adjust column widths
    const range = XLSX.utils.decode_range(worksheet['!ref'] || '');
    const colWidths = [];
    for (let col = range.s.c; col <= range.e.c; col++) {
      let maxWidth = 10;
      for (let row = range.s.r; row <= range.e.r; row++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        const cell = worksheet[cellAddress];
        if (cell && cell.v) {
          maxWidth = Math.max(maxWidth, cell.v.toString().length);
        }
      }
      colWidths.push({ width: Math.min(maxWidth + 2, 50) });
    }
    worksheet['!cols'] = colWidths;

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(data, `Users_Export_${new Date().toISOString().split('T')[0]}.xlsx`);
    success('Users data exported to Excel successfully!');
  };

  const exportJobsToExcel = () => {
    const exportData = filteredJobs.map(job => ({
      'Job ID': job.jobId,
      'Title': job.title,
      'Company': job.companyName,
      'Location': job.location,
      'Employment Type': job.employmentType,
      'Experience Level': job.experienceLevel,
      'Job Category': job.jobCategory,
      'Number of Openings': job.numberOfOpenings,
      'Salary Min': job.salaryRange?.min || '',
      'Salary Max': job.salaryRange?.max || '',
      'Status': job.status,
      'Is Active': job.isActive ? 'Yes' : 'No',
      'Posted Date': new Date(job.createdAt).toLocaleDateString(),
      'Last Date to Apply': job.lastDateToApply ? new Date(job.lastDateToApply).toLocaleDateString() : '',
      'Posted By': `${job.postedBy?.firstName || ''} ${job.postedBy?.lastName || ''}`.trim(),
      'Views': job.views || 0,
      'Applications': job.applications || 0
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Jobs');
    
    // Auto-adjust column widths
    const range = XLSX.utils.decode_range(worksheet['!ref'] || '');
    const colWidths = [];
    for (let col = range.s.c; col <= range.e.c; col++) {
      let maxWidth = 10;
      for (let row = range.s.r; row <= range.e.r; row++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        const cell = worksheet[cellAddress];
        if (cell && cell.v) {
          maxWidth = Math.max(maxWidth, cell.v.toString().length);
        }
      }
      colWidths.push({ width: Math.min(maxWidth + 2, 50) });
    }
    worksheet['!cols'] = colWidths;

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(data, `Jobs_Export_${new Date().toISOString().split('T')[0]}.xlsx`);
    success('Jobs data exported to Excel successfully!');
  };

  const exportTicketsToExcel = () => {
    const exportData = filteredTickets.map(ticket => ({
      'Ticket ID': ticket.bugId,
      'Issue Description': ticket.issueDescription,
      'Bug Type': ticket.bugType,
      'Module': ticket.module,
      'Priority': ticket.priority,
      'Status': ticket.status,
      'Reporter Name': ticket.reporterName,
      'Reporter Email': ticket.reporterEmail,
      'Created Date': new Date(ticket.createdAt).toLocaleDateString(),
      'Last Updated': ticket.updatedAt ? new Date(ticket.updatedAt).toLocaleDateString() : '',
      'Steps to Reproduce': ticket.stepsToReproduce || '',
      'Expected Result': ticket.expectedResult || '',
      'Actual Result': ticket.actualResult || '',
      'Browser': ticket.browserInfo || '',
      'OS': ticket.osInfo || '',
      'Additional Info': ticket.additionalInfo || ''
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Support Tickets');
    
    // Auto-adjust column widths
    const range = XLSX.utils.decode_range(worksheet['!ref'] || '');
    const colWidths = [];
    for (let col = range.s.c; col <= range.e.c; col++) {
      let maxWidth = 10;
      for (let row = range.s.r; row <= range.e.r; row++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        const cell = worksheet[cellAddress];
        if (cell && cell.v) {
          maxWidth = Math.max(maxWidth, cell.v.toString().length);
        }
      }
      colWidths.push({ width: Math.min(maxWidth + 2, 50) });
    }
    worksheet['!cols'] = colWidths;

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(data, `Support_Tickets_Export_${new Date().toISOString().split('T')[0]}.xlsx`);
    success('Support tickets data exported to Excel successfully!');
  };

  // Computed properties for filtering
  const filteredTickets = allTickets.filter(ticket => {
    const matchesSearch = ticketSearchTerm === '' || 
      ticket.bugId.toLowerCase().includes(ticketSearchTerm.toLowerCase()) ||
      ticket.issueDescription.toLowerCase().includes(ticketSearchTerm.toLowerCase()) ||
      ticket.reporterName.toLowerCase().includes(ticketSearchTerm.toLowerCase()) ||
      ticket.reporterEmail.toLowerCase().includes(ticketSearchTerm.toLowerCase());
    
    const matchesStatus = ticketStatusFilter === 'all' || ticket.status === ticketStatusFilter;
    const matchesPriority = ticketPriorityFilter === 'all' || ticket.priority === ticketPriorityFilter;
    
    return matchesSearch && matchesStatus && matchesPriority;
  });

  // Color functions for tickets
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Open': return 'bg-red-100 text-red-800 border-red-200';
      case 'In Progress': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Fixed': return 'bg-primary-light text-green-800 border-green-200';
      case 'Closed': return 'bg-gray-100 text-secondary border-border';
      case 'Rejected': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-secondary border-border';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Low': return 'bg-primary-light text-blue-800 border-blue-200';
      case 'Medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'High': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'Critical': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-secondary border-border';
    }
  };

  useEffect(() => {
    loadDashboardStats();
    fetchHackathons();
  }, []);

  useEffect(() => {
    setSearchParams({ tab: activeTab });
  }, [activeTab, setSearchParams]);

  const loadDashboardStats = async () => {
    try {
      setLoading(true);
      
      // Fetch stats from multiple sources with pagination
      const [dashboardData, jobsData, pendingData, allUsersData, ticketsData, hackathonStats] = await Promise.all([
        dashboardAPI.getAdminDashboard(),
        adminJobAPI.getJobs({ 
          page: jobPage, 
          limit: 5000 // Fetch more jobs to avoid pagination issues for now
        }).catch((err) => {
          console.error('Error fetching jobs:', err);
          return { success: false, data: { jobs: [], counts: { pending: 0, approved: 0, rejected: 0 } } };
        }),
        fetchPendingUsers(),
        fetchAllUsers(),
        fetchTickets(),
        hackathonAPI.getHackathonStats().catch((err) => {
          console.error('Error fetching hackathon stats:', err);
          return { total: 0, active: 0, upcoming: 0, completed: 0 };
        })
      ]);

      console.log('Jobs API response:', jobsData);

      // Set user data
      setPendingUsers(pendingData);
      setAllUsers(allUsersData);

      // Calculate user stats from actual data
      const userStats = {
        total: allUsersData.length,
        pending: pendingData.length,
        approved: allUsersData.filter((user: User) => user.isApproved).length,
        rejected: allUsersData.filter((user: User) => user.rejectionReason).length
      };

      // Set job data
      const jobs = jobsData.data?.jobs || [];
      setAllJobs(jobs);
      setPendingJobs(jobs.filter((job: Job) => job.status === 'pending'));
      
      console.log('Jobs data:', jobs);
      console.log('Jobs data structure:', jobsData);
      
      // Use backend counts if available, otherwise calculate from jobs array
      const backendCounts = jobsData.data?.counts || {};
      const jobStats = {
        total: jobs.length,
        pending: backendCounts.pending || jobs.filter((job: any) => job.status === 'pending').length,
        approved: backendCounts.approved || jobs.filter((job: any) => job.status === 'approved').length,
        rejected: backendCounts.rejected || jobs.filter((job: any) => job.status === 'rejected').length,
      };
      
      console.log('Calculated job stats:', jobStats);

      // Calculate real ticket stats from fetched data
      const ticketStats = {
        total: ticketsData.length,
        open: ticketsData.filter((ticket: any) => ticket.status === 'Open').length,
        inProgress: ticketsData.filter((ticket: any) => ticket.status === 'In Progress').length,
        resolved: ticketsData.filter((ticket: any) => ticket.status === 'Fixed' || ticket.status === 'Closed').length,
      };
      
      console.log('Calculated ticket stats:', ticketStats);

      setStats({
        users: userStats,
        jobs: jobStats,
        tickets: ticketStats,
        hackathons: hackathonStats
      });

    } catch (err: any) {
      console.error('Error loading dashboard stats:', err);
      error('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingUsers = async () => {
    try {
      const response = await dashboardAPI.getPendingUsers();
      return response.data.users || [];
    } catch (error: any) {
      console.error('Error fetching pending users:', error);
      return [];
    }
  };

  const fetchAllUsers = async () => {
    try {
      // Fetch all users with a high limit to avoid pagination issues
      const response = await dashboardAPI.getUsers({ 
        limit: 5000, // Fetch up to 5000 users to ensure we get all of them
        status: 'all' 
      });
      return response.data.users || [];
    } catch (error) {
      console.error('Error fetching users:', error);
      return [];
    }
  };

  const fetchTickets = async () => {
    try {
      setTicketLoading(true);
      // Fetch tickets with higher limit to avoid pagination issues for now
      const response = await fetch('/api/bug-reports/admin?limit=5000', {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        const ticketsData = data.data?.bugReports || data.data || [];
        const tickets = Array.isArray(ticketsData) ? ticketsData : [];
        setAllTickets(tickets);
        
        // Update pagination info if available
        if (data.data?.pagination) {
          setTicketTotal(data.data.pagination.totalReports);
          setTicketTotalPages(data.data.pagination.totalPages);
        }
        
        return tickets;
      } else {
        throw new Error(data.message || 'Failed to fetch tickets');
      }
    } catch (err) {
      console.error('Error fetching tickets:', err);
      error('Failed to fetch tickets');
      setAllTickets([]);
      return [];
    } finally {
      setTicketLoading(false);
    }
  };

  const deleteTicket = (ticketId: string) => {
    setTicketToDelete(ticketId);
    setShowDeleteTicketDialog(true);
  };

  const confirmDeleteTicket = async () => {
    if (!ticketToDelete) return;

    try {
      setDeleting(ticketToDelete);
      const response = await fetch(`/api/bug-reports/${ticketToDelete}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Remove ticket from the list
      setAllTickets(prev => prev.filter(ticket => ticket._id !== ticketToDelete));
      
      loadDashboardStats();
      success('Ticket deleted successfully!');
      setShowDeleteTicketDialog(false);
      setTicketToDelete(null);
    } catch (err) {
      console.error('Error deleting ticket:', err);
      error('Failed to delete ticket');
    } finally {
      setDeleting(null);
      setShowDeleteTicketDialog(false);
      setTicketToDelete(null);
    }
  };

  // Analytics functions
  const fetchAnalyticsData = async () => {
    setAnalyticsLoading(true);
    try {
      // Fetch all analytics data in parallel
      const [registrationTrend, aspirantInterests, experience, industry, placedAspirants] = await Promise.all([
        fetchRegistrationTrend(),
        fetchAspirantInterests(),
        fetchExperienceData(),
        fetchIndustryData(),
        fetchPlacedAspirants()
      ]);
      
      setRegistrationTrendData(registrationTrend);
      setAspirantInterestsData(aspirantInterests);
      setExperienceData(experience);
      setIndustryData(industry);
      setPlacedAspirantsData(placedAspirants);
    } catch (err) {
      console.error('Error fetching analytics data:', err);
      error('Failed to fetch analytics data');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const fetchRegistrationTrend = async () => {
    try {
      const params = new URLSearchParams();
      params.append('period', trendPeriod);
      if (customDateRange.start) params.append('startDate', customDateRange.start);
      if (customDateRange.end) params.append('endDate', customDateRange.end);

      const response = await fetch(`/api/analytics/registration-trend?${params.toString()}`, {
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to fetch registration trend');
      const data = await response.json();
      return data.data || [];
    } catch (err) {
      console.error('Error fetching registration trend:', err);
      return [];
    }
  };

  const fetchAspirantInterests = async () => {
    try {
      const response = await fetch('/api/analytics/aspirant-interests', {
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to fetch aspirant interests');
      const data = await response.json();
      return data.data || [];
    } catch (err) {
      console.error('Error fetching aspirant interests:', err);
      return [];
    }
  };

  const fetchExperienceData = async () => {
    try {
      const response = await fetch('/api/analytics/experience-distribution', {
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to fetch experience data');
      const data = await response.json();
      return data.data || [];
    } catch (err) {
      console.error('Error fetching experience data:', err);
      return [];
    }
  };

  const fetchIndustryData = async () => {
    try {
      const response = await fetch('/api/analytics/industry-distribution', {
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to fetch industry data');
      const data = await response.json();
      return data.data || [];
    } catch (err) {
      console.error('Error fetching industry data:', err);
      return [];
    }
  };

  const fetchPlacedAspirants = async () => {
    try {
      const response = await fetch('/api/analytics/placed-aspirants', {
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to fetch placed aspirants');
      const data = await response.json();
      return data.data || [];
    } catch (err) {
      console.error('Error fetching placed aspirants:', err);
      return [];
    }
  };

  // Clarity Analytics loading function
  const loadClarityMetrics = async () => {
    setClarityLoading(true);
    try {
      // In a real implementation, you would call Clarity's reporting API
      // For now, we use mock data from the service
      const metrics = clarityService.getMockAnalyticsData();
      setClarityMetrics(metrics);
    } catch (err) {
      console.error('Error loading Clarity metrics:', err);
      error('Failed to load Clarity analytics');
    } finally {
      setClarityLoading(false);
    }
  };

  const approveUser = async (userId: string) => {
    try {
      setApproving(userId);
      await dashboardAPI.approveUser(userId);
      setPendingUsers(prev => prev.filter(user => user.id !== userId));
      loadDashboardStats();
      success('User approved successfully!');
    } catch (error: any) {
      console.error('Error approving user:', error);
      error('Failed to approve user');
    } finally {
      setApproving(null);
    }
  };

  const rejectUser = async (userId: string, reason: string) => {
    try {
      setRejecting(userId);
      await dashboardAPI.rejectUser(userId, reason);
      setPendingUsers(prev => prev.filter(user => user.id !== userId));
      setAllUsers(prev => prev.map(user => 
        user.id === userId ? { ...user, isApproved: false, rejectionReason: reason, isActive: false } : user
      ));
      setShowRejectDialog(false);
      setRejectionReason('');
      loadDashboardStats();
      success('User rejected successfully!');
    } catch (error: any) {
      console.error('Error rejecting user:', error);
      error('Failed to reject user');
    } finally {
      setRejecting(null);
    }
  };

  const deleteUser = (userId: string) => {
    setUserToDelete(userId);
    setShowDeleteUserDialog(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      setDeleting(userToDelete);
      await dashboardAPI.deleteUser(userToDelete);
      setPendingUsers(prev => prev.filter(user => user.id !== userToDelete));
      setAllUsers(prev => prev.filter(user => user.id !== userToDelete));
      if (selectedUser && selectedUser.id === userToDelete) {
        setShowUserDetails(false);
        setSelectedUser(null);
      }
      loadDashboardStats();
      success('User deleted successfully!');
      setShowDeleteUserDialog(false);
      setUserToDelete(null);
    } catch (error: any) {
      console.error('Error deleting user:', error);
      error('Failed to delete user');
    } finally {
      setDeleting(null);
      setShowDeleteUserDialog(false);
      setUserToDelete(null);
    }
  };

  const toggleUserStatus = async (userId: string) => {
    try {
      setToggling(userId);
      const response = await dashboardAPI.toggleUserStatus(userId);
      const updateUser = (users: User[]) => 
        users.map(user => 
          user.id === userId ? { ...user, isActive: response.data.user.isActive } : user
        );
      setPendingUsers(updateUser);
      setAllUsers(updateUser);
      if (selectedUser && selectedUser.id === userId) {
        setSelectedUser({ ...selectedUser, isActive: response.data.user.isActive });
      }
      success(`User ${response.data.user.isActive ? 'activated' : 'deactivated'} successfully!`);
    } catch (error: any) {
      console.error('Error toggling user status:', error);
      error('Failed to toggle user status');
    } finally {
      setToggling(null);
    }
  };

  const openRejectDialog = (user: User) => {
    setSelectedUser(user);
    setShowRejectDialog(true);
  };

  const filteredUsers = allUsers.filter(user => {
    const searchTermLower = searchTerm.toLowerCase();
    const matchesSearch = searchTerm === '' || 
      user.fullName.toLowerCase().includes(searchTermLower) ||
      user.email.toLowerCase().includes(searchTermLower) ||
      user.address?.toLowerCase().includes(searchTermLower) ||
      user.phone?.toLowerCase().includes(searchTermLower) ||
      user.primarySkills?.some(skill => skill.toLowerCase().includes(searchTermLower)) ||
      user.secondarySkills?.some(skill => skill.toLowerCase().includes(searchTermLower)) ||
      user.careerGoals?.dreamCompany?.toLowerCase().includes(searchTermLower) ||
      user.careerGoals?.targetRole?.toLowerCase().includes(searchTermLower) ||
      user.education?.institution?.toLowerCase().includes(searchTermLower) ||
      user.education?.degree?.toLowerCase().includes(searchTermLower) ||
      user.education?.fieldOfStudy?.toLowerCase().includes(searchTermLower) ||
      user.professionalInfo?.currentCompany?.toLowerCase().includes(searchTermLower) ||
      user.professionalInfo?.currentPosition?.toLowerCase().includes(searchTermLower) ||
      user.professionalInfo?.industry?.toLowerCase().includes(searchTermLower) ||
      user.experience?.some(exp => 
        exp.company?.toLowerCase().includes(searchTermLower) ||
        exp.position?.toLowerCase().includes(searchTermLower) ||
        exp.description?.toLowerCase().includes(searchTermLower)
      ) ||
      user.skills?.technical?.some(skill => skill.toLowerCase().includes(searchTermLower)) ||
      user.skills?.soft?.some(skill => skill.toLowerCase().includes(searchTermLower)) ||
      user.skills?.certifications?.some(cert => cert.toLowerCase().includes(searchTermLower)) ||
      user.achievements?.some(achievement => achievement.toLowerCase().includes(searchTermLower));
    
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'pending' && !user.isApproved && !user.rejectionReason) ||
      (statusFilter === 'approved' && user.isApproved) ||
      (statusFilter === 'rejected' && user.rejectionReason);
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    return matchesSearch && matchesStatus && matchesRole;
  });

  // Job management functions
  const approveJob = async (jobId: string) => {
    try {
      setJobApproving(jobId);
      await adminJobAPI.approveJob(jobId);
      
      // Update job lists
      setAllJobs(prev => prev.map(job => 
        job._id === jobId ? { ...job, status: 'approved' as const, isActive: true } : job
      ));
      setPendingJobs(prev => prev.filter(job => job._id !== jobId));
      
      loadDashboardStats();
      success('Job approved successfully!');
    } catch (error: any) {
      console.error('Error approving job:', error);
      error('Failed to approve job');
    } finally {
      setJobApproving(null);
    }
  };

  const rejectJob = async (jobId: string, reason: string) => {
    try {
      setJobRejecting(jobId);
      await adminJobAPI.rejectJob(jobId, reason);
      
      // Update job lists
      setAllJobs(prev => prev.map(job => 
        job._id === jobId ? { ...job, status: 'rejected' as const, isActive: false } : job
      ));
      setPendingJobs(prev => prev.filter(job => job._id !== jobId));
      
      setShowJobRejectDialog(false);
      setJobRejectionReason('');
      
      loadDashboardStats();
      success('Job rejected successfully!');
    } catch (error: any) {
      console.error('Error rejecting job:', error);
      error('Failed to reject job');
    } finally {
      setJobRejecting(null);
    }
  };

  const deleteJob = (jobId: string) => {
    const job = allJobs.find(j => j._id === jobId);
    setJobToDelete(job);
    setShowDeleteJobDialog(true);
  };

  const confirmDeleteJob = async () => {
    if (!jobToDelete) return;

    try {
      setDeleting(jobToDelete._id);
      
      console.log('Attempting to delete job:', jobToDelete._id);
      
      const response = await adminJobAPI.deleteJob(jobToDelete._id);
      console.log('Delete job response:', response);
      
      // Remove job from all lists
      setAllJobs(prev => prev.filter(job => job._id !== jobToDelete._id));
      setPendingJobs(prev => prev.filter(job => job._id !== jobToDelete._id));
      
      loadDashboardStats();
      success(response.data?.message || 'Job deleted successfully!');
      setShowDeleteJobDialog(false);
      setJobToDelete(null);
    } catch (err: any) {
      console.error('Error deleting job:', err);
      console.error('Error details:', err.response?.data);
      
      if (err.response?.status === 404) {
        error('Job not found or may have been already deleted.');
      } else if (err.response?.status === 403) {
        error('Permission denied. Admin access required.');
      } else {
        error('Failed to delete job: ' + (err.response?.data?.message || err.message));
      }
    } finally {
      setDeleting(null);
      setShowDeleteJobDialog(false);
      setJobToDelete(null);
    }
  };

  const toggleJobActive = async (jobId: string) => {
    try {
      setToggling(jobId);
      const response = await adminJobAPI.toggleJobActive(jobId);
      
      console.log('Toggle job response:', response);
      
      // Backend returns the job object directly in response.data
      const updatedJob = response.data;
      const newActiveStatus = updatedJob?.isActive;
      
      if (newActiveStatus !== undefined) {
        // Update job lists
        setAllJobs(prev => prev.map(job => 
          job._id === jobId ? { ...job, isActive: newActiveStatus } : job
        ));
        setPendingJobs(prev => prev.map(job => 
          job._id === jobId ? { ...job, isActive: newActiveStatus } : job
        ));
        
        success(`Job ${newActiveStatus ? 'activated' : 'deactivated'} successfully!`);
      } else {
        throw new Error('Invalid response structure from toggle endpoint');
      }
    } catch (err: any) {
      console.error('Error toggling job status:', err);
      error('Failed to toggle job status');
    } finally {
      setToggling(null);
    }
  };

  // Job permission checks
  const canDeleteJob = (job: Job) => {
    // Business Rules:
    // - Admin can delete any job (since this is admin dashboard)
    // - Job poster can only delete their own jobs if not approved yet
    // - Once approved by admin, only admin can delete
    
    // For admin dashboard, admin can delete any job
    // But we'll show a warning for approved jobs since they're live
    return true;
  };

  const canToggleJobActive = (job: Job) => {
    // Admin can toggle any job, job poster can toggle their own jobs
    // Only makes sense for approved jobs
    return job.status === 'approved';
  };

  const openJobRejectDialog = (job: Job) => {
    setSelectedJob(job);
    setShowJobRejectDialog(true);
  };

  // Payment management functions
  const fetchHackathons = async () => {
    try {
      const response = await hackathonAPI.getHackathons({ limit: 100 });
      setHackathons(response.hackathons || []);
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to fetch hackathons');
    }
  };

  const fetchHackathonPayments = async (hackathonId: string) => {
    try {
      const response = await adminPaymentAPI.getHackathonPayments(hackathonId);
      setHackathonPayments(response.data.payments || []);
      setHackathonRegistrations(response.data.registrations || []);
      setPaymentStats(response.data.stats || {});
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to fetch hackathon payments');
    }
  };

  const handleMarkAsPaid = async (userId: string, hackathonId: string, userName: string, hackathonTitle: string) => {
    setShowPaymentDialog({
      userId,
      action: 'approve',
      hackathonId,
      userName,
      hackathonTitle
    });
  };

  const handleRevertPayment = async (userId: string, hackathonId: string, userName: string, hackathonTitle: string) => {
    setShowPaymentDialog({
      userId,
      action: 'revert',
      hackathonId,
      userName,
      hackathonTitle
    });
  };

  const confirmPaymentAction = async () => {
    if (!showPaymentDialog) return;

    const { userId, action, hackathonId } = showPaymentDialog;
    const actionId = `${userId}_${hackathonId}`;

    try {
      setProcessingPayment(actionId);
      setIsPageDisabled(true);

      console.log('Payment action:', { userId, action, hackathonId });
      console.log('Current payments:', hackathonPayments);
      console.log('Current registrations:', hackathonRegistrations);

      if (action === 'approve') {
        await adminPaymentAPI.markUserAsPaid(userId, hackathonId);
        success('User marked as paid and registered successfully!');
      } else {
        await adminPaymentAPI.revertPayment(userId, hackathonId);
        success('Payment and registration reverted successfully!');
      }

      // Refresh payments data
      await fetchHackathonPayments(hackathonId);
      setShowPaymentDialog(null);
    } catch (err: any) {
      console.error('Payment action error:', err);
      console.error('Error response:', err.response?.data);
      error(err.response?.data?.message || `Failed to ${action} payment`);
    } finally {
      setProcessingPayment(null);
      setIsPageDisabled(false);
    }
  };

  const getUserPaymentStatus = (userId: string, hackathonId: string) => {
    if (!Array.isArray(hackathonPayments) || !Array.isArray(hackathonRegistrations)) {
      return { status: 'not_paid', payment: null, registration: null };
    }
    
    const payment = hackathonPayments.find(p => p.user?._id === userId && p.hackathon === hackathonId);
    const registration = hackathonRegistrations.find(r => r.user?._id === userId && r.hackathon === hackathonId);
    
    if (payment?.status === 'completed' && registration?.status === 'confirmed') {
      return { status: 'paid', payment, registration };
    } else if (payment?.status === 'refunded' || registration?.status === 'withdrawn') {
      return { status: 'reverted', payment, registration };
    } else {
      return { status: 'not_paid', payment: null, registration: null };
    }
  };

  const filteredJobs = allJobs.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(jobSearchTerm.toLowerCase()) ||
                         job.companyName.toLowerCase().includes(jobSearchTerm.toLowerCase()) ||
                         job.location.toLowerCase().includes(jobSearchTerm.toLowerCase());
    const matchesStatus = jobStatusFilter === 'all' || job.status === jobStatusFilter;
    return matchesSearch && matchesStatus;
  });

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'jobs', label: 'Jobs', icon: Briefcase },
    { id: 'tickets', label: 'Tickets', icon: Bug },
    { id: 'analytics', label: 'Analytics', icon: TrendingUp }
  ];

  // Pagination component
  const Pagination = ({ 
    currentPage, 
    totalPages, 
    totalItems,
    itemsPerPage,
    onPageChange,
    entityName 
  }: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    onPageChange: (page: number) => void;
    entityName: string;
  }) => {
    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
      const pages = [];
      const showPages = 5; // Show 5 page numbers at most
      
      let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
      let endPage = Math.min(totalPages, startPage + showPages - 1);
      
      if (endPage - startPage < showPages - 1) {
        startPage = Math.max(1, endPage - showPages + 1);
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      return pages;
    };

    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    return (
      <div className="flex items-center justify-between px-4 py-3 bg-surface border-t border-border sm:px-6">
        <div className="flex justify-between items-center w-full">
          <div className="text-sm text-gray-700">
            Showing <span className="font-medium">{startItem}</span> to{' '}
            <span className="font-medium">{endItem}</span> of{' '}
            <span className="font-medium">{totalItems}</span> {entityName}
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1"
            >
              Previous
            </Button>
            
            {getPageNumbers().map(page => (
              <Button
                key={page}
                variant={page === currentPage ? "default" : "outline"}
                size="sm"
                onClick={() => onPageChange(page)}
                className={`px-3 py-1 ${
                  page === currentPage 
                    ? 'bg-primary text-white' 
                    : 'hover:bg-background'
                }`}
              >
                {page}
              </Button>
            ))}
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1"
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Pagination handlers
  const handleUserPageChange = (page: number) => {
    setUserPage(page);
    // For now, we'll use client-side pagination since we're fetching all data
    // Later, we can implement server-side pagination for better performance
  };

  const handleJobPageChange = (page: number) => {
    setJobPage(page);
    // Implement server-side pagination for jobs if needed
    loadDashboardStats();
  };

  const handleTicketPageChange = (page: number) => {
    setTicketPage(page);
    // Implement server-side pagination for tickets if needed
    fetchTickets();
  };

  // Client-side pagination for filtered data
  const getPaginatedUsers = (users: User[], page: number = userPage) => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedUsers = users.slice(startIndex, endIndex);
    const totalPages = Math.ceil(users.length / itemsPerPage);
    
    // Update pagination state
    if (userTotalPages !== totalPages) {
      setUserTotalPages(totalPages);
      setUserTotal(users.length);
    }
    
    return paginatedUsers;
  };

  const getPaginatedJobs = (jobs: Job[], page: number = jobPage) => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedJobs = jobs.slice(startIndex, endIndex);
    const totalPages = Math.ceil(jobs.length / itemsPerPage);
    
    // Update pagination state
    if (jobTotalPages !== totalPages) {
      setJobTotalPages(totalPages);
      setJobTotal(jobs.length);
    }
    
    return paginatedJobs;
  };

  const getPaginatedTickets = (tickets: BugReport[], page: number = ticketPage) => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedTickets = tickets.slice(startIndex, endIndex);
    const totalPages = Math.ceil(tickets.length / itemsPerPage);
    
    // Update pagination state
    if (ticketTotalPages !== totalPages) {
      setTicketTotalPages(totalPages);
      setTicketTotal(tickets.length);
    }
    
    return paginatedTickets;
  };

  // Update filtered data to use pagination
  const filteredUsersWithPagination = getPaginatedUsers(filteredUsers);
  const filteredJobsWithPagination = getPaginatedJobs(filteredJobs);
  const allTicketsWithPagination = getPaginatedTickets(filteredTickets);

  if (loading) {
    return (
      <LoadingSpinner 
        text="Loading Admin Dashboard"
        subtitle="Preparing your admin workspace..."
        size="lg"
      />
    );
  }

  return (
    <div className={`min-h-screen bg-background ${isPageDisabled ? 'pointer-events-none opacity-50' : ''}`}>
      {isPageDisabled && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex items-center space-x-3">
            <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
            <span className="text-lg font-medium">Processing payment...</span>
          </div>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-6 py-6 space-y-4">
        {/* Header */}
        <div className="space-y-4">
          <div>
            <h1 className="text-3xl font-bold text-secondary">Admin Dashboard</h1>
            <p className="text-sm text-gray-600 mt-1">
              Manage platform operations, user approvals, jobs, and support tickets
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button 
              onClick={loadDashboardStats} 
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-2"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh Data
            </Button>
            <Button
              onClick={() => navigate('/admin/content')}
              className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-2"
            >
              <Settings className="h-4 w-4 mr-2" />
              Manage Site Content
            </Button>
            <Button
              onClick={() => navigate('/admin/ai-prompts')}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2"
            >
              <Bot className="h-4 w-4 mr-2" />
              AI Prompts
            </Button>
            <Button
              onClick={() => navigate('/admin/events')}
              className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-2"
            >
              <Calendar className="h-4 w-4 mr-2" />
              Event Management
            </Button>

            <Button
              onClick={() => navigate('/admin/hackathons')}
              className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-2"
            >
              <Trophy className="h-4 w-4 mr-2" />
              Hackathon Management
            </Button>
            <Button
              onClick={() => navigate('/admin/highlights')}
              className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2"
            >
              <Trophy className="h-4 w-4 mr-2" />
              Highlights Management
            </Button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-surface rounded-lg shadow border border-border p-1">
          <div className="flex space-x-1">
            {tabs.map((tab) => {
              const IconComponent = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-1 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                    activeTab === tab.id
                      ? 'bg-primary text-white shadow-md'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-secondary'
                  }`}
                >
                  <IconComponent className="h-4 w-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-secondary">Platform Overview</h2>
            
            {/* Overview Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Users Overview */}
              <Card className="bg-surface border-border shadow-lg rounded-xl hover:shadow-xl transition-all duration-300 group overflow-hidden">
                <CardHeader className="bg-gradient-to-br from-blue-50 to-blue-100 px-4 py-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-bold text-secondary">Users</CardTitle>
                    <div className="p-2 bg-primary rounded-lg group-hover:bg-primary-dark transition-colors">
                      <Users className="h-4 w-4 text-white" />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-secondary mb-2">{stats?.users.total || 0}</div>
                  <div className="space-y-1 mb-3">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Pending</span>
                      <span className="font-semibold text-amber-600">{stats?.users.pending || 0}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Approved</span>
                      <span className="font-semibold text-primary">{stats?.users.approved || 0}</span>
                    </div>
                  </div>
                  <Button
                    onClick={() => setActiveTab('users')}
                    className="w-full bg-primary hover:bg-primary-dark text-white text-xs py-1.5"
                  >
                    Manage Users
                    <ArrowRight className="h-3 w-3 ml-1" />
                  </Button>
                </CardContent>
              </Card>

              {/* Jobs Overview */}
              <Card className="bg-surface border-border shadow-lg rounded-xl hover:shadow-xl transition-all duration-300 group overflow-hidden">
                <CardHeader className="bg-gradient-to-br from-green-50 to-green-100 px-4 py-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-bold text-secondary">Jobs</CardTitle>
                    <div className="p-2 bg-primary rounded-lg group-hover:bg-primary-dark transition-colors">
                      <Briefcase className="h-4 w-4 text-white" />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-secondary mb-2">{stats?.jobs.total || 0}</div>
                  <div className="space-y-1 mb-3">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Pending</span>
                      <span className="font-semibold text-amber-600">{stats?.jobs.pending || 0}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Approved</span>
                      <span className="font-semibold text-primary">{stats?.jobs.approved || 0}</span>
                    </div>
                  </div>
                  <Button
                    onClick={() => setActiveTab('jobs')}
                    className="w-full bg-primary hover:bg-primary-dark text-white text-xs py-1.5"
                  >
                    Manage Jobs
                    <ArrowRight className="h-3 w-3 ml-1" />
                  </Button>
                </CardContent>
              </Card>

              {/* Tickets Overview */}
              <Card className="bg-surface border-border shadow-lg rounded-xl hover:shadow-xl transition-all duration-300 group overflow-hidden">
                <CardHeader className="bg-gradient-to-br from-green-50 to-green-100 px-4 py-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-bold text-secondary">Support Tickets</CardTitle>
                    <div className="p-2 bg-primary rounded-lg group-hover:bg-primary-dark transition-colors">
                      <Bug className="h-4 w-4 text-white" />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-secondary mb-2">{stats?.tickets.total || 0}</div>
                  <div className="space-y-1 mb-3">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Open</span>
                      <span className="font-semibold text-amber-600">{stats?.tickets.open || 0}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Resolved</span>
                      <span className="font-semibold text-primary">{stats?.tickets.resolved || 0}</span>
                    </div>
                  </div>
                  <Button
                    onClick={() => setActiveTab('tickets')}
                    className="w-full bg-primary hover:bg-primary-dark text-white text-xs py-1.5"
                  >
                    Manage Tickets
                    <ArrowRight className="h-3 w-3 ml-1" />
                  </Button>
                </CardContent>
              </Card>

              {/* Hackathons Overview */}
              <Card className="bg-surface border-border shadow-lg rounded-xl hover:shadow-xl transition-all duration-300 group overflow-hidden">
                <CardHeader className="bg-gradient-to-br from-indigo-50 to-indigo-100 px-4 py-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-bold text-secondary">Hackathons</CardTitle>
                    <div className="p-2 bg-indigo-600 rounded-lg group-hover:bg-indigo-700 transition-colors">
                      <Trophy className="h-4 w-4 text-white" />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold text-secondary mb-2">{stats?.hackathons.total || 0}</div>
                  <div className="space-y-1 mb-3">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Active</span>
                      <span className="font-semibold text-indigo-600">{stats?.hackathons.active || 0}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Upcoming</span>
                      <span className="font-semibold text-indigo-600">{stats?.hackathons.upcoming || 0}</span>
                    </div>
                  </div>
                  <Button
                    onClick={() => navigate('/admin/hackathons')}
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white text-xs py-1.5"
                  >
                    Manage Hackathons
                    <ArrowRight className="h-3 w-3 ml-1" />
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-secondary">User Management</h2>
              <Button
                onClick={exportUsersToExcel}
                className="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <Download className="h-4 w-4 mr-2" />
                Export to Excel
              </Button>
            </div>

            {/* User Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card 
                className="bg-gradient-to-br from-blue-50 to-blue-100 cursor-pointer hover:shadow-lg transition-all duration-300 hover:scale-105"
                onClick={() => setStatusFilter('all')}
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xl font-bold text-primary">{stats?.users.total || 0}</div>
                      <p className="text-blue-800 font-medium text-xs">Total Users</p>
                    </div>
                    <Users className="h-5 w-5 text-primary" />
                  </div>
                </CardContent>
              </Card>

              <Card 
                className="bg-gradient-to-br from-amber-50 to-amber-100 cursor-pointer hover:shadow-lg transition-all duration-300 hover:scale-105"
                onClick={() => setStatusFilter('pending')}
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xl font-bold text-amber-600">
                        {stats?.users.pending || 0}
                        {(() => {
                          const pendingUsers = allUsers.filter(user => !user.isApproved && !user.rejectionReason);
                          const emailUnverifiedCount = pendingUsers.filter(user => !user.isEmailVerified).length;
                          return emailUnverifiedCount > 0 ? ` (${emailUnverifiedCount})` : '';
                        })()}
                      </div>
                      <p className="text-amber-800 font-medium text-xs">Pending Approval</p>
                    </div>
                    <Clock className="h-5 w-5 text-amber-600" />
                  </div>
                </CardContent>
              </Card>

              <Card 
                className="bg-gradient-to-br from-green-50 to-green-100 cursor-pointer hover:shadow-lg transition-all duration-300 hover:scale-105"
                onClick={() => setStatusFilter('approved')}
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xl font-bold text-primary">{stats?.users.approved || 0}</div>
                      <p className="text-green-800 font-medium text-xs">Approved Users</p>
                    </div>
                    <UserCheck className="h-5 w-5 text-primary" />
                  </div>
                </CardContent>
              </Card>

              <Card 
                className="bg-gradient-to-br from-red-50 to-red-100 cursor-pointer hover:shadow-lg transition-all duration-300 hover:scale-105"
                onClick={() => setStatusFilter('rejected')}
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xl font-bold text-red-600">{stats?.users.rejected || 0}</div>
                      <p className="text-red-800 font-medium text-xs">Rejected</p>
                    </div>
                    <UserX className="h-5 w-5 text-red-600" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* All Users Section */}
            <Card className="bg-surface/90 backdrop-blur-sm border-slate-200 shadow-lg">
              <CardHeader className="border-b border-slate-100 pb-3">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <CardTitle className="flex items-center gap-3 text-xl font-semibold text-slate-900">
                      <div className="p-2 bg-primary-light rounded-lg">
                        <Users className="h-5 w-5 text-primary" />
                      </div>
                      All Users
                    </CardTitle>
                    <CardDescription className="text-slate-600">
                      View and manage all users on the platform
                    </CardDescription>
                  </div>
                  <Badge variant="secondary" className="bg-primary-light text-blue-800 border-blue-200">
                    {filteredUsers.length} total users
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-3">
                {/* Filters */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4 relative">
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                    <Input
                      id="search"
                      placeholder="Search by name, email, skills, location..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 bg-surface/80 border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="bg-surface/80 border-slate-200 z-[9999] relative">
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent className="z-[9999] relative bg-surface shadow-2xl border-2">
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={roleFilter} onValueChange={setRoleFilter}>
                    <SelectTrigger className="bg-surface/80 border-slate-200 z-[9999] relative">
                      <SelectValue placeholder="Filter by role" />
                    </SelectTrigger>
                    <SelectContent className="z-[9999] relative bg-surface shadow-2xl border-2">
                      <SelectItem value="all">All Roles</SelectItem>
                      <SelectItem value="aspirant">Aspirant</SelectItem>
                      <SelectItem value="mentor">Mentor</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select 
                    value={selectedHackathon || "none"} 
                    onValueChange={(value) => {
                      if (value === "none") {
                        setSelectedHackathon("");
                      } else {
                        setSelectedHackathon(value);
                        if (value) {
                          fetchHackathonPayments(value);
                        }
                      }
                    }}
                  >
                    <SelectTrigger className="bg-surface/80 border-slate-200 z-[9999] relative">
                      <SelectValue placeholder="Select hackathon for payments" />
                    </SelectTrigger>
                    <SelectContent className="z-[9999] relative bg-surface shadow-2xl border-2">
                      <SelectItem value="none">No hackathon selected</SelectItem>
                      {hackathons.map((hackathon) => (
                        <SelectItem key={hackathon._id} value={hackathon._id}>
                          {hackathon.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Payment Stats */}
                {selectedHackathon && paymentStats && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <div className="flex gap-4 text-sm justify-center">
                      <div className="bg-white px-3 py-2 rounded border">
                        <span className="font-medium text-green-600">
                          {paymentStats.completedPayments} Paid
                        </span>
                      </div>
                      <div className="bg-white px-3 py-2 rounded border">
                        <span className="font-medium text-red-600">
                          {paymentStats.refundedPayments} Reverted
                        </span>
                      </div>
                      <div className="bg-white px-3 py-2 rounded border">
                        <span className="font-medium text-blue-600">
                          {paymentStats.totalRegistrations} Total
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Users Grid */}
                <div className="space-y-4">
                  {filteredUsersWithPagination.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="p-4 bg-slate-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                        <Users className="h-8 w-8 text-slate-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">No Users Found</h3>
                      <p className="text-slate-600 max-w-sm mx-auto">
                        {searchTerm || statusFilter !== 'all' || roleFilter !== 'all' 
                          ? 'Try adjusting your search or filters to find users.'
                          : 'No users have registered on the platform yet.'
                        }
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {filteredUsersWithPagination.map((user) => (
                        <div key={user.id} className="bg-gradient-to-r from-slate-50 to-blue-50 border border-slate-200 rounded-xl p-6 hover:shadow-md transition-all duration-200">
                          <div className="space-y-4">
                            <div className="space-y-3">
                              <div className="flex items-center gap-3">
                                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                                  <span className="text-white font-semibold text-lg">
                                    {user.fullName.split(' ').map(n => n[0]).join('').toUpperCase()}
                                  </span>
                                </div>
                                <div className="space-y-1">
                                  <h3 className="font-semibold text-lg text-slate-900">{user.fullName}</h3>
                                  <div className="flex items-center gap-2">
                                    <Badge variant={user.role === 'mentor' ? 'default' : 'secondary'} 
                                      className={`text-xs ${user.role === 'mentor' ? 'bg-primary text-white' : ''}`}>
                                      {user.role}
                                    </Badge>
                                    <span className="text-xs text-slate-500">•</span>
                                    <span className="text-xs text-slate-500">{user.email}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="space-y-2 text-sm text-slate-600">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="font-medium">Status:</span>
                                  {user.isApproved ? (
                                    <Badge variant="default" className="bg-emerald-100 text-emerald-800 border-emerald-200 text-xs">
                                      Approved
                                    </Badge>
                                  ) : user.rejectionReason ? (
                                    <Badge variant="destructive" className="bg-red-100 text-red-800 border-red-200 text-xs">
                                      Rejected
                                    </Badge>
                                  ) : (
                                    <Badge variant="secondary" className="bg-amber-100 text-amber-800 border-amber-200 text-xs">
                                      Pending
                                    </Badge>
                                  )}
                                  <Badge variant="outline" className={`text-xs ${
                                    user.isActive 
                                      ? 'bg-green-50 text-green-700 border-green-300' 
                                      : 'bg-background text-gray-600 border-gray-300'
                                  }`}>
                                    {user.isActive ? 'Active' : 'Inactive'}
                                  </Badge>
                                  <Badge variant="outline" className={`text-xs ${
                                    user.isEmailVerified 
                                      ? 'bg-blue-50 text-blue-700 border-blue-300' 
                                      : 'bg-primary-light text-primary border-orange-300'
                                  }`}>
                                    {user.isEmailVerified ? 'Email Verified' : 'Email Unverified'}
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">Registration #:</span>
                                  <span className="text-xs font-mono text-primary font-semibold">
                                    {user.registrationNumber || 'N/A'}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">Joined:</span>
                                  <span className="text-xs">{new Date(user.createdAt).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                  })}</span>
                                </div>
                              </div>
                              {user.rejectionReason && (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                                  <p className="text-xs text-red-700">
                                    <span className="font-medium">Rejection Reason:</span> {user.rejectionReason}
                                  </p>
                                </div>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-2 pt-4 border-t border-slate-200">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedUser(user);
                                  setShowUserDetails(true);
                                }}
                                className="bg-secondary hover:bg-secondary-light text-white border-secondary min-w-[110px]"
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                View Details
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedUser(user);
                                  setShowFullProfile(true);
                                }}
                                className="bg-purple-50 hover:bg-purple-100 border-purple-300 text-purple-700 min-w-[120px]"
                              >
                                <Users className="h-4 w-4 mr-2" />
                                Full Profile
                              </Button>
                              {user.role !== 'admin' && (
                                <>
                                  {!user.isApproved && !user.rejectionReason && (
                                    <>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => approveUser(user.id)}
                                        disabled={approving === user.id}
                                        className="bg-green-50 hover:bg-primary-light border-green-300 text-green-700 min-w-[80px]"
                                      >
                                        {approving === user.id ? (
                                          <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                                        ) : (
                                          <CheckCircle className="h-4 w-4 mr-2" />
                                        )}
                                        Approve
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => openRejectDialog(user)}
                                        disabled={rejecting === user.id}
                                        className="bg-red-50 hover:bg-red-100 border-red-300 text-red-700 min-w-[80px]"
                                      >
                                        {rejecting === user.id ? (
                                          <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                                        ) : (
                                          <XCircle className="h-4 w-4 mr-2" />
                                        )}
                                        Reject
                                      </Button>
                                    </>
                                  )}
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => toggleUserStatus(user.id)}
                                    disabled={toggling === user.id}
                                    className={`min-w-[100px] ${
                                      user.isActive 
                                        ? 'bg-amber-100 hover:bg-amber-200 border-amber-400 text-amber-800' 
                                        : 'bg-green-50 hover:bg-primary-light border-green-300 text-green-700'
                                    }`}
                                  >
                                    {toggling === user.id ? (
                                      <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                                    ) : user.isActive ? (
                                      <PowerOff className="h-4 w-4 mr-2" />
                                    ) : (
                                      <Power className="h-4 w-4 mr-2" />
                                    )}
                                    {user.isActive ? 'Deactivate' : 'Activate'}
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => deleteUser(user.id)}
                                    disabled={deleting === user.id}
                                    className="bg-red-50 hover:bg-red-100 border-red-300 text-red-700 min-w-[80px]"
                                  >
                                    {deleting === user.id ? (
                                      <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                                    ) : (
                                      <Trash2 className="h-4 w-4 mr-2" />
                                    )}
                                    Delete
                                  </Button>
                                </>
                              )}

                              {/* Payment Management Buttons */}
                              {selectedHackathon && user.role === 'aspirant' && Array.isArray(hackathonPayments) && Array.isArray(hackathonRegistrations) && (
                                <>
                                  {(() => {
                                    const paymentStatus = getUserPaymentStatus(user.id, selectedHackathon);
                                    const actionId = `${user.id}_${selectedHackathon}`;
                                    const isProcessing = processingPayment === actionId;
                                    
                                    if (paymentStatus.status === 'paid') {
                                      return (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => handleRevertPayment(
                                            user.id, 
                                            selectedHackathon, 
                                            user.fullName, 
                                            hackathons.find(h => h._id === selectedHackathon)?.title || 'Hackathon'
                                          )}
                                          disabled={isProcessing || isPageDisabled}
                                          className="bg-orange-50 hover:bg-orange-100 border-orange-300 text-orange-700 min-w-[120px]"
                                        >
                                          {isProcessing ? (
                                            <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                                          ) : (
                                            <span className="mr-2">↩️</span>
                                          )}
                                          Revert ₹250
                                        </Button>
                                      );
                                    } else {
                                      return (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => handleMarkAsPaid(
                                            user.id, 
                                            selectedHackathon, 
                                            user.fullName, 
                                            hackathons.find(h => h._id === selectedHackathon)?.title || 'Hackathon'
                                          )}
                                          disabled={isProcessing || isPageDisabled}
                                          className="bg-green-50 hover:bg-green-100 border-green-300 text-green-700 min-w-[100px]"
                                        >
                                          {isProcessing ? (
                                            <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                                          ) : (
                                            <span className="mr-2">💰</span>
                                          )}
                                          Paid ₹250
                                        </Button>
                                      );
                                    }
                                  })()}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
              
              {/* Users Pagination */}
              <Pagination
                currentPage={userPage}
                totalPages={userTotalPages}
                totalItems={userTotal || filteredUsers.length}
                itemsPerPage={itemsPerPage}
                onPageChange={handleUserPageChange}
                entityName="users"
              />
            </Card>
          </div>
        )}

        {activeTab === 'jobs' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-secondary">Job Management</h2>
              <Button
                onClick={exportJobsToExcel}
                className="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <Download className="h-4 w-4 mr-2" />
                Export to Excel
              </Button>
            </div>

            {/* Job Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card 
                className="bg-gradient-to-br from-blue-50 to-blue-100 cursor-pointer hover:shadow-lg transition-all duration-300 hover:scale-105"
                onClick={() => setJobStatusFilter('all')}
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xl font-bold text-primary">{stats?.jobs.total || 0}</div>
                      <p className="text-blue-800 font-medium text-xs">Total Jobs</p>
                    </div>
                    <Briefcase className="h-5 w-5 text-primary" />
                  </div>
                </CardContent>
              </Card>

              <Card 
                className="bg-gradient-to-br from-amber-50 to-amber-100 cursor-pointer hover:shadow-lg transition-all duration-300 hover:scale-105"
                onClick={() => setJobStatusFilter('pending')}
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xl font-bold text-amber-600">{stats?.jobs.pending || 0}</div>
                      <p className="text-amber-800 font-medium text-xs">Pending Review</p>
                    </div>
                    <Clock className="h-5 w-5 text-amber-600" />
                  </div>
                </CardContent>
              </Card>

              <Card 
                className="bg-gradient-to-br from-green-50 to-green-100 cursor-pointer hover:shadow-lg transition-all duration-300 hover:scale-105"
                onClick={() => setJobStatusFilter('approved')}
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xl font-bold text-primary">{stats?.jobs.approved || 0}</div>
                      <p className="text-green-800 font-medium text-xs">Approved</p>
                    </div>
                    <CheckCircle className="h-5 w-5 text-primary" />
                  </div>
                </CardContent>
              </Card>

              <Card 
                className="bg-gradient-to-br from-red-50 to-red-100 cursor-pointer hover:shadow-lg transition-all duration-300 hover:scale-105"
                onClick={() => setJobStatusFilter('rejected')}
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xl font-bold text-red-600">{stats?.jobs.rejected || 0}</div>
                      <p className="text-red-800 font-medium text-xs">Rejected</p>
                    </div>
                    <XCircle className="h-5 w-5 text-red-600" />
                  </div>
                </CardContent>
              </Card>
            </div>



            {/* All Jobs Section */}
            <Card className="bg-surface/90 backdrop-blur-sm border-slate-200 shadow-lg">
              <CardHeader className="border-b border-slate-100 pb-3">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <CardTitle className="flex items-center gap-3 text-xl font-semibold text-slate-900">
                      <div className="p-2 bg-primary-light rounded-lg">
                        <Briefcase className="h-5 w-5 text-primary" />
                      </div>
                      All Jobs
                    </CardTitle>
                    <CardDescription className="text-slate-600">
                      Manage job postings and applications
                    </CardDescription>
                  </div>
                  <Badge variant="secondary" className="bg-primary-light text-blue-800 border-blue-200">
                    {filteredJobs.length} total jobs
                  </Badge>
                </div>
              </CardHeader>
              
              <CardContent className="p-6">
                {/* Job Filters */}
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Search jobs by title, company, or location..."
                        value={jobSearchTerm}
                        onChange={(e) => setJobSearchTerm(e.target.value)}
                        className="pl-10 bg-surface/80 border-slate-200"
                      />
                    </div>
                  </div>
                  <Select value={jobStatusFilter} onValueChange={setJobStatusFilter}>
                    <SelectTrigger className="bg-surface/80 border-slate-200 w-full sm:w-48">
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {loading ? (
                  <div className="flex justify-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin text-primary" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredJobsWithPagination.length === 0 ? (
                      <div className="text-center py-12">
                        <div className="p-4 bg-slate-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                          <Briefcase className="h-8 w-8 text-slate-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-slate-900 mb-2">No Jobs Found</h3>
                        <p className="text-slate-600 max-w-sm mx-auto">
                          {jobSearchTerm || jobStatusFilter !== 'all' 
                            ? 'Try adjusting your search or filters to find jobs.'
                            : 'No job postings have been created yet.'
                          }
                        </p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-4">
                        {filteredJobsWithPagination.map((job) => (
                          <div key={job._id} className="bg-gradient-to-r from-slate-50 to-blue-50 border border-slate-200 rounded-xl p-6 hover:shadow-md transition-all duration-200">
                            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                              <div className="flex-1 space-y-3">
                                <div className="space-y-2">
                                  <h3 className="font-semibold text-lg text-slate-900">{job.title}</h3>
                                  <div className="flex items-center gap-2 text-sm">
                                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                      {job.companyName}
                                    </Badge>
                                    <span className="text-slate-500">•</span>
                                    <span className="text-slate-600">{job.location}</span>
                                    <span className="text-slate-500">•</span>
                                    <span className="text-slate-600">{job.employmentType}</span>
                                  </div>
                                </div>
                                <div className="flex flex-wrap items-center gap-2 text-sm">
                                  <span className="font-medium text-slate-700">Status:</span>
                                  {job.status === 'approved' ? (
                                    <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
                                      Approved
                                    </Badge>
                                  ) : job.status === 'rejected' ? (
                                    <Badge variant="destructive" className="bg-red-100 text-red-800 border-red-200">
                                      Rejected
                                    </Badge>
                                  ) : (
                                    <Badge variant="secondary" className="bg-amber-100 text-amber-800 border-amber-200">
                                      Pending
                                    </Badge>
                                  )}
                                  
                                  {/* Active/Inactive Status for approved jobs */}
                                  {job.status === 'approved' && (
                                    <>
                                      <span className="text-slate-500">•</span>
                                      <Badge variant="outline" className={`${
                                        job.isActive 
                                          ? 'bg-green-50 text-green-700 border-green-300' 
                                          : 'bg-background text-gray-600 border-gray-300'
                                      }`}>
                                        {job.isActive ? 'Active' : 'Inactive'}
                                      </Badge>
                                    </>
                                  )}
                                  
                                  <span className="text-slate-500">•</span>
                                  <span className="text-xs text-slate-500">
                                    Posted on {new Date(job.createdAt).toLocaleDateString()}
                                  </span>
                                  
                                  {/* Job poster information */}
                                  <span className="text-slate-500">•</span>
                                  <span className="text-xs text-slate-500">
                                    By {job.postedBy?.firstName} {job.postedBy?.lastName}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedJob(job);
                                    setShowJobDetails(true);
                                  }}
                                  className="bg-secondary hover:bg-secondary-light text-white border-secondary"
                                >
                                  <Eye className="h-4 w-4 mr-2" />
                                  View Details
                                </Button>
                                
                                {/* Active/Inactive Toggle - Only for approved jobs */}
                                {job.status === 'approved' && canToggleJobActive(job) && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => toggleJobActive(job._id)}
                                    disabled={toggling === job._id}
                                    className={`min-w-[100px] ${
                                      job.isActive 
                                        ? 'bg-amber-100 hover:bg-amber-200 border-amber-400 text-amber-800' 
                                        : 'bg-green-50 hover:bg-primary-light border-green-300 text-green-700'
                                    }`}
                                  >
                                    {toggling === job._id ? (
                                      <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                                    ) : job.isActive ? (
                                      <PowerOff className="h-4 w-4 mr-2" />
                                    ) : (
                                      <Power className="h-4 w-4 mr-2" />
                                    )}
                                    {job.isActive ? 'Deactivate' : 'Activate'}
                                  </Button>
                                )}
                                
                                {/* Approval/Rejection buttons for pending jobs */}
                                {job.status === 'pending' && (
                                  <>
                                    <Button
                                      size="sm"
                                      onClick={() => approveJob(job._id)}
                                      disabled={jobApproving === job._id}
                                      className="bg-primary hover:bg-primary-dark text-white"
                                    >
                                      {jobApproving === job._id ? (
                                        <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                                      ) : (
                                        <CheckCircle className="h-4 w-4 mr-2" />
                                      )}
                                      Approve
                                    </Button>
                                    <Button
                                      variant="destructive"
                                      size="sm"
                                      onClick={() => openJobRejectDialog(job)}
                                      disabled={jobRejecting === job._id}
                                      className="bg-red-600 hover:bg-red-700 text-white"
                                    >
                                      {jobRejecting === job._id ? (
                                        <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                                      ) : (
                                        <XCircle className="h-4 w-4 mr-2" />
                                      )}
                                      Reject
                                    </Button>
                                  </>
                                )}
                                
                                {/* Delete button - Only for non-approved jobs or admin */}
                                {canDeleteJob(job) && (
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => deleteJob(job._id)}
                                    disabled={deleting === job._id}
                                    className="bg-red-800 hover:bg-red-900 text-white"
                                  >
                                    {deleting === job._id ? (
                                      <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                                    ) : (
                                      <Trash2 className="h-4 w-4 mr-2" />
                                    )}
                                    Delete
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
              
              {/* Jobs Pagination */}
              <Pagination
                currentPage={jobPage}
                totalPages={jobTotalPages}
                totalItems={jobTotal || filteredJobs.length}
                itemsPerPage={itemsPerPage}
                onPageChange={handleJobPageChange}
                entityName="jobs"
              />
            </Card>
          </div>
        )}

        {activeTab === 'tickets' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-secondary">Bug Reports & Tickets</h2>
              <Button
                onClick={exportTicketsToExcel}
                className="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <Download className="h-4 w-4 mr-2" />
                Export to Excel
              </Button>
            </div>

            {/* Ticket Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xl font-bold text-primary">{stats?.tickets.total || 0}</div>
                      <p className="text-blue-800 font-medium text-xs">Total Tickets</p>
                    </div>
                    <Bug className="h-5 w-5 text-primary" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-amber-50 to-amber-100">
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xl font-bold text-amber-600">{stats?.tickets.open || 0}</div>
                      <p className="text-amber-800 font-medium text-xs">Open</p>
                    </div>
                    <Clock className="h-5 w-5 text-amber-600" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-50 to-purple-100">
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xl font-bold text-purple-600">{stats?.tickets.inProgress || 0}</div>
                      <p className="text-purple-800 font-medium text-xs">In Progress</p>
                    </div>
                    <Activity className="h-5 w-5 text-purple-600" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-green-50 to-green-100">
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xl font-bold text-primary">{stats?.tickets.resolved || 0}</div>
                      <p className="text-green-800 font-medium text-xs">Resolved</p>
                    </div>
                    <CheckCircle className="h-5 w-5 text-primary" />
                  </div>
                </CardContent>
              </Card>
            </div>



            {/* All Tickets Section */}
            <Card className="bg-surface/90 backdrop-blur-sm border-slate-200 shadow-lg">
              <CardHeader className="border-b border-slate-100 pb-3">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <CardTitle className="flex items-center gap-3 text-xl font-semibold text-slate-900">
                      <div className="p-2 bg-red-100 rounded-lg">
                        <Bug className="h-5 w-5 text-red-600" />
                      </div>
                      All Tickets
                    </CardTitle>
                    <CardDescription className="text-slate-600">
                      Manage bug reports and user feedback
                    </CardDescription>
                  </div>
                  <Badge variant="secondary" className="bg-red-100 text-red-800 border-red-200">
                    {filteredTickets.length} total tickets
                  </Badge>
                </div>
              </CardHeader>
              
              <CardContent className="p-6">
                {/* Ticket Filters */}
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Search tickets by ID, description, or reporter..."
                        value={ticketSearchTerm}
                        onChange={(e) => setTicketSearchTerm(e.target.value)}
                        className="pl-10 bg-surface/80 border-slate-200"
                      />
                    </div>
                  </div>
                  <Select value={ticketStatusFilter} onValueChange={setTicketStatusFilter}>
                    <SelectTrigger className="bg-surface/80 border-slate-200 w-full sm:w-48">
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="Open">Open</SelectItem>
                      <SelectItem value="In Progress">In Progress</SelectItem>
                      <SelectItem value="Fixed">Fixed</SelectItem>
                      <SelectItem value="Closed">Closed</SelectItem>
                      <SelectItem value="Rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={ticketPriorityFilter} onValueChange={setTicketPriorityFilter}>
                    <SelectTrigger className="bg-surface/80 border-slate-200 w-full sm:w-48">
                      <SelectValue placeholder="Filter by priority" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Priorities</SelectItem>
                      <SelectItem value="Low">Low</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="High">High</SelectItem>
                      <SelectItem value="Critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {ticketLoading ? (
                  <div className="flex justify-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin text-red-600" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    {allTicketsWithPagination.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Bug className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                        <p>No tickets found matching your criteria.</p>
                      </div>
                    ) : (
                      allTicketsWithPagination.map((ticket) => (
                        <div key={ticket._id} className="border rounded-lg p-6 hover:shadow-lg transition-shadow">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="text-lg font-semibold text-secondary">
                                  #{ticket.bugId}
                                </h3>
                                <Badge className={getStatusColor(ticket.status)}>
                                  {ticket.status}
                                </Badge>
                                <Badge className={getPriorityColor(ticket.priority)}>
                                  {ticket.priority}
                                </Badge>
                                <Badge variant="outline" className="text-xs">
                                  {ticket.module}
                                </Badge>
                              </div>
                              <p className="text-gray-700 mb-3 line-clamp-2">
                                {ticket.issueDescription}
                              </p>
                                                             <div className="flex items-center gap-6 text-sm text-gray-500">
                                 <div className="flex items-center gap-1">
                                   <Users className="h-4 w-4" />
                                   <span>{ticket.reporterName}</span>
                                 </div>
                                 <div className="flex items-center gap-1">
                                   <Clock className="h-4 w-4" />
                                   <span>{new Date(ticket.createdAt).toLocaleDateString()}</span>
                                 </div>
                               </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedTicket(ticket);
                                  setShowTicketDetails(true);
                                }}
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                View Details
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => deleteTicket(ticket._id)}
                                disabled={deleting === ticket._id}
                                className="bg-red-800 hover:bg-red-900 text-white"
                              >
                                {deleting === ticket._id ? (
                                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                                ) : (
                                  <Trash2 className="h-4 w-4 mr-2" />
                                )}
                                Delete
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </CardContent>
              
              {/* Tickets Pagination */}
              <Pagination
                currentPage={ticketPage}
                totalPages={ticketTotalPages}
                totalItems={ticketTotal || allTickets.length}
                itemsPerPage={itemsPerPage}
                onPageChange={handleTicketPageChange}
                entityName="tickets"
              />
            </Card>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-secondary">Analytics Dashboard</h2>
            </div>

            {/* Analytics Sub-tabs */}
            <div className="bg-surface rounded-lg shadow border border-border p-1">
              <div className="flex space-x-1">
                <button
                  onClick={() => {
                    setAnalyticsTab('site');
                    if (analyticsTab !== 'site') {
                      loadClarityMetrics();
                    }
                  }}
                  className={`flex items-center space-x-1 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                    analyticsTab === 'site'
                      ? 'bg-primary text-white shadow-md'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Activity className="h-4 w-4" />
                  <span>Site Analytics</span>
                </button>
                <button
                  onClick={() => {
                    setAnalyticsTab('data');
                    if (analyticsTab !== 'data') {
                      fetchAnalyticsData();
                    }
                  }}
                  className={`flex items-center space-x-1 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                    analyticsTab === 'data'
                      ? 'bg-primary text-white shadow-md'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <PieChart className="h-4 w-4" />
                  <span>Data Analytics</span>
                </button>
              </div>
            </div>

            {/* Site Analytics - Live Analytics Only */}
            {analyticsTab === 'site' && (
              <div className="space-y-8">
                {/* Live Analytics Access */}
                <Card className="bg-gradient-to-br from-emerald-50 to-teal-100 border-emerald-200">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <div className="p-2 bg-emerald-600 rounded-lg">
                        <ExternalLink className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <span className="text-emerald-900">Live Analytics Dashboard</span>
                        <p className="text-sm font-normal text-emerald-700 mt-1">
                          Access real-time data from Microsoft Clarity
                        </p>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <Button
                        variant="outline"
                        className="h-20 flex flex-col items-center justify-center bg-surface hover:bg-emerald-50 border-emerald-200"
                        onClick={() => clarityService.openClaritySection('dashboard')}
                      >
                        <BarChart3 className="h-6 w-6 text-emerald-600 mb-2" />
                        <span className="font-medium text-emerald-900">Main Dashboard</span>
                        <span className="text-xs text-emerald-600">Overview & Metrics</span>
                      </Button>

                      <Button
                        variant="outline"
                        className="h-20 flex flex-col items-center justify-center bg-surface hover:bg-emerald-50 border-emerald-200"
                        onClick={() => clarityService.openClaritySection('recordings')}
                      >
                        <Eye className="h-6 w-6 text-emerald-600 mb-2" />
                        <span className="font-medium text-emerald-900">Session Recordings</span>
                        <span className="text-xs text-emerald-600">Watch User Sessions</span>
                      </Button>

                      <Button
                        variant="outline"
                        className="h-20 flex flex-col items-center justify-center bg-surface hover:bg-emerald-50 border-emerald-200"
                        onClick={() => clarityService.openClaritySection('heatmaps')}
                      >
                        <Target className="h-6 w-6 text-emerald-600 mb-2" />
                        <span className="font-medium text-emerald-900">Heatmaps</span>
                        <span className="text-xs text-emerald-600">Click & Scroll Maps</span>
                      </Button>

                      <Button
                        variant="outline"
                        className="h-20 flex flex-col items-center justify-center bg-surface hover:bg-emerald-50 border-emerald-200"
                        onClick={() => clarityService.openClaritySection('insights')}
                      >
                        <TrendingUp className="h-6 w-6 text-emerald-600 mb-2" />
                        <span className="font-medium text-emerald-900">Insights</span>
                        <span className="text-xs text-emerald-600">Smart Analytics</span>
                      </Button>

                      <Button
                        variant="outline"
                        className="h-20 flex flex-col items-center justify-center bg-surface hover:bg-emerald-50 border-emerald-200"
                        onClick={() => clarityService.openClaritySection('liveView')}
                      >
                        <Activity className="h-6 w-6 text-emerald-600 mb-2" />
                        <span className="font-medium text-emerald-900">Live View</span>
                        <span className="text-xs text-emerald-600">Real-time Sessions</span>
                      </Button>

                      <Button
                        variant="outline"
                        className="h-20 flex flex-col items-center justify-center bg-surface hover:bg-emerald-50 border-emerald-200"
                        onClick={() => clarityService.openClaritySection('settings')}
                      >
                        <Settings className="h-6 w-6 text-emerald-600 mb-2" />
                        <span className="font-medium text-emerald-900">Settings</span>
                        <span className="text-xs text-emerald-600">Project Configuration</span>
                      </Button>
                    </div>

                    <div className="mt-6 p-4 bg-surface/60 rounded-lg border border-emerald-200">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                        <span className="text-sm font-medium text-emerald-800">
                          Live tracking active for udyogapramoda.org
                        </span>
                      </div>
                      <p className="text-xs text-emerald-600 mt-1 ml-5">
                        Project ID: sgrnp2cu67 • All user interactions are being tracked
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Data Analytics */}
            {analyticsTab === 'data' && (
              <div className="space-y-8">
                {analyticsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="h-8 w-8 animate-spin text-primary" />
                    <span className="ml-2 text-gray-600">Loading analytics data...</span>
                  </div>
                ) : (
                  <>
                    {/* Registration Trend */}
                    <Card className="bg-surface border-border shadow-lg">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle className="flex items-center gap-2">
                            <TrendingUp className="h-5 w-5 text-primary" />
                            Registration Trend
                          </CardTitle>
                          <div className="flex items-center gap-2">
                            <Select value={trendPeriod} onValueChange={setTrendPeriod}>
                              <SelectTrigger className="w-32 z-[9999] relative">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="z-[9999] relative bg-surface shadow-2xl border-2">
                                <SelectItem value="day">Daily</SelectItem>
                                <SelectItem value="week">Weekly</SelectItem>
                                <SelectItem value="month">Monthly</SelectItem>
                                <SelectItem value="custom">Custom</SelectItem>
                              </SelectContent>
                            </Select>
                            {trendPeriod === 'custom' && (
                              <div className="flex items-center gap-2">
                                <Input
                                  type="date"
                                  value={customDateRange.start}
                                  onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
                                  className="w-40"
                                />
                                <span className="text-gray-500">to</span>
                                <Input
                                  type="date"
                                  value={customDateRange.end}
                                  onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
                                  className="w-40"
                                />
                              </div>
                            )}
                            <Button
                              onClick={fetchRegistrationTrend}
                              className="bg-green-600 hover:bg-green-700 text-white px-6 py-2"
                            >
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Refresh
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <RegistrationTrendChart 
                          data={registrationTrendData} 
                          loading={analyticsLoading}
                        />
                      </CardContent>
                    </Card>

                    {/* Aspirant Interests */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <Card className="bg-surface border-border shadow-lg">
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <PieChart className="h-5 w-5 text-primary" />
                            Aspirant Interests
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <AspirantInterestsChart 
                            data={aspirantInterestsData} 
                            loading={analyticsLoading}
                          />
                        </CardContent>
                      </Card>

                      <Card className="bg-surface border-border shadow-lg">
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Activity className="h-5 w-5 text-purple-600" />
                            Experience Distribution
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ExperienceDistributionChart 
                            data={experienceData} 
                            loading={analyticsLoading}
                          />
                        </CardContent>
                      </Card>
                    </div>

                    {/* Industry Distribution */}
                    <Card className="bg-surface border-border shadow-lg">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Building2 className="h-5 w-5 text-indigo-600" />
                          Industry Distribution
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="h-80 flex items-center justify-center bg-background rounded-lg">
                          <div className="text-center">
                            <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-600">Industry distribution chart</p>
                            <div className="mt-4 grid grid-cols-2 gap-4">
                              {industryData.map((item, index) => (
                                <div key={index} className="flex items-center justify-between text-sm p-2 bg-surface rounded border">
                                  <span>{item.industry}</span>
                                  <span className="font-medium">{item.count}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Placed Aspirants */}
                    <Card className="bg-surface border-border shadow-lg">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Trophy className="h-5 w-5 text-primary" />
                          Placed Aspirants
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {placedAspirantsData.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                              <Trophy className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                              <p>No placement data available yet.</p>
                            </div>
                          ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              {placedAspirantsData.map((aspirant, index) => (
                                <div key={index} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                                  <div className="flex items-center gap-3 mb-2">
                                    <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center">
                                      <Trophy className="h-5 w-5 text-white" />
                                    </div>
                                    <div>
                                      <h3 className="font-medium text-secondary">{aspirant.name}</h3>
                                      <p className="text-sm text-gray-600">{aspirant.company}</p>
                                    </div>
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    <p>Position: {aspirant.position}</p>
                                    <p>Month: {aspirant.month}</p>
                                    <p>Package: {aspirant.package}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* User Details Dialog */}
      <Dialog open={showUserDetails} onOpenChange={setShowUserDetails}>
        <DialogContent className="max-w-2xl bg-surface/95 backdrop-blur-sm border-slate-200">
          <DialogHeader className="border-b border-slate-100 pb-4">
            <DialogTitle className="text-xl font-semibold text-slate-900">User Details</DialogTitle>
            <DialogDescription className="text-slate-600">
              Detailed information about the selected user
            </DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-6 py-4">
              <div className="flex items-center space-x-4 pb-4 border-b border-slate-100">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-semibold text-xl">
                    {selectedUser.fullName.split(' ').map(n => n[0]).join('').toUpperCase()}
                  </span>
                </div>
                <div className="space-y-1">
                  <h3 className="text-lg font-semibold text-slate-900">{selectedUser.fullName}</h3>
                  <div className="flex items-center gap-2">
                    <Badge variant={selectedUser.role === 'mentor' ? 'default' : 'secondary'}>
                      {selectedUser.role}
                    </Badge>
                    {selectedUser.isApproved ? (
                      <Badge variant="default" className="bg-emerald-100 text-emerald-800">
                        Approved
                      </Badge>
                    ) : selectedUser.rejectionReason ? (
                      <Badge variant="destructive" className="bg-red-100 text-red-800 border-red-200">
                        Rejected
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                        Pending
                      </Badge>
                    )}
                    <Badge variant="outline" className={`${
                      selectedUser.isActive 
                        ? 'bg-green-50 text-green-700 border-green-300' 
                        : 'bg-background text-gray-600 border-gray-300'
                    }`}>
                      {selectedUser.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Full Name</Label>
                    <p className="text-sm text-slate-900 mt-1">{selectedUser.fullName}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Email</Label>
                    <p className="text-sm text-slate-900 mt-1">{selectedUser.email}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Registration Number</Label>
                    <p className="text-sm text-slate-900 mt-1 font-mono text-primary font-semibold">
                      {selectedUser.registrationNumber || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Phone</Label>
                    <p className="text-sm text-slate-900 mt-1">{selectedUser.phone}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Address</Label>
                    <p className="text-sm text-slate-900 mt-1">{selectedUser.address}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Email Verified</Label>
                    <p className="text-sm text-slate-900 mt-1">
                      {selectedUser.isEmailVerified ? (
                        <span className="text-emerald-600">✓ Verified</span>
                      ) : (
                        <span className="text-amber-600">⚠ Not Verified</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Registration Date</Label>
                    <p className="text-sm text-slate-900 mt-1">
                      {new Date(selectedUser.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                  {selectedUser.rejectionReason && (
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Rejection Reason</Label>
                      <p className="text-sm text-red-600 mt-1">{selectedUser.rejectionReason}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject User Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="max-w-md bg-surface/95 backdrop-blur-sm border-slate-200">
          <DialogHeader className="border-b border-slate-100 pb-4">
            <DialogTitle className="text-xl font-semibold text-slate-900 flex items-center gap-2">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              Reject User
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              Provide a reason for rejecting this user's application
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {selectedUser && (
              <div className="flex items-center space-x-3 p-4 bg-slate-50 rounded-lg">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">
                    {selectedUser.fullName.split(' ').map(n => n[0]).join('').toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-slate-900">{selectedUser.fullName}</p>
                  <p className="text-sm text-slate-500">{selectedUser.email}</p>
                </div>
              </div>
            )}
            <div className="space-y-3">
              <Label htmlFor="rejection-reason" className="text-sm font-medium text-slate-700">
                Rejection Reason
              </Label>
              <Textarea
                id="rejection-reason"
                placeholder="Please provide a detailed reason for rejection..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={4}
                className="bg-surface/80 border-slate-200 focus:border-red-500 focus:ring-red-500 resize-none"
              />
              <p className="text-xs text-slate-500">
                Minimum 10 characters required
              </p>
            </div>
          </div>
          <DialogFooter className="border-t border-slate-100 pt-4">
            <Button 
              variant="outline" 
              onClick={() => setShowRejectDialog(false)}
              className="bg-surface/80 border-slate-300 hover:bg-surface"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => selectedUser && rejectUser(selectedUser.id, rejectionReason)}
              disabled={!rejectionReason.trim() || rejectionReason.length < 10}
              className="bg-red-600 hover:bg-red-700"
            >
              <XCircle className="h-4 w-4 mr-2" />
              Reject User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Full Profile Dialog */}
      <Dialog open={showFullProfile} onOpenChange={setShowFullProfile}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto bg-surface/95 backdrop-blur-sm border-slate-200">
          <DialogHeader className="border-b border-slate-100 pb-4">
            <DialogTitle className="text-2xl font-semibold text-slate-900 flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="h-6 w-6 text-purple-600" />
              </div>
              Complete User Profile
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              Comprehensive view of user information and profile details
            </DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-8 py-6">
              {/* Basic Information */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-blue-700">Full Name</p>
                    <p className="text-blue-900">{selectedUser.fullName}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-blue-700">Email</p>
                    <p className="text-blue-900">{selectedUser.email}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-blue-700">Phone</p>
                    <p className="text-blue-900">{selectedUser.phone || 'Not provided'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-blue-700">Role</p>
                    <Badge variant="outline" className="bg-primary-light text-blue-800 border-blue-300">
                      {selectedUser.role}
                    </Badge>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-blue-700">Registration Number</p>
                    <p className="text-blue-900 font-mono">{selectedUser.registrationNumber || 'N/A'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-blue-700">Joined</p>
                    <p className="text-blue-900">{new Date(selectedUser.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}</p>
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  <p className="text-sm font-medium text-blue-700">Address</p>
                  <p className="text-blue-900">{selectedUser.address || 'Not provided'}</p>
                </div>
              </div>

              {/* Status Information */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-green-900 mb-4 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5" />
                  Account Status
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-green-700">Account Status</p>
                    <Badge variant="outline" className={`${
                      selectedUser.isActive 
                        ? 'bg-primary-light text-green-800 border-green-300' 
                        : 'bg-gray-100 text-gray-600 border-gray-300'
                    }`}>
                      {selectedUser.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-green-700">Email Status</p>
                    <Badge variant="outline" className={`${
                      selectedUser.isEmailVerified 
                        ? 'bg-primary-light text-blue-800 border-blue-300' 
                        : 'bg-orange-100 text-orange-800 border-orange-300'
                    }`}>
                      {selectedUser.isEmailVerified ? 'Verified' : 'Unverified'}
                    </Badge>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-green-700">Approval Status</p>
                    {selectedUser.isApproved ? (
                      <Badge variant="outline" className="bg-emerald-100 text-emerald-800 border-emerald-300">
                        Approved
                      </Badge>
                    ) : selectedUser.rejectionReason ? (
                      <Badge variant="outline" className="bg-red-100 text-red-800 border-red-300">
                        Rejected
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
                        Pending
                      </Badge>
                    )}
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-green-700">Resume</p>
                    {selectedUser.resumeUrl ? (
                      <a 
                        href={selectedUser.resumeUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:text-blue-800 text-sm underline"
                      >
                        View Resume
                      </a>
                    ) : (
                      <span className="text-gray-500 text-sm">Not uploaded</span>
                    )}
                  </div>
                </div>
                {selectedUser.rejectionReason && (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm font-medium text-red-700 mb-1">Rejection Reason</p>
                    <p className="text-red-600">{selectedUser.rejectionReason}</p>
                  </div>
                )}
              </div>

              {/* Skills & Expertise */}
              {(selectedUser.primarySkills || selectedUser.secondarySkills || selectedUser.skills) && (
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-purple-900 mb-4 flex items-center gap-2">
                    <Target className="h-5 w-5" />
                    Skills & Expertise
                  </h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {selectedUser.primarySkills && selectedUser.primarySkills.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-purple-700">Primary Skills</p>
                        <div className="flex flex-wrap gap-2">
                          {selectedUser.primarySkills.map((skill, index) => (
                            <Badge key={index} variant="secondary" className="bg-purple-100 text-purple-800">
                              {skill}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedUser.secondarySkills && selectedUser.secondarySkills.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-purple-700">Secondary Skills</p>
                        <div className="flex flex-wrap gap-2">
                          {selectedUser.secondarySkills.map((skill, index) => (
                            <Badge key={index} variant="outline" className="bg-purple-50 text-purple-700 border-purple-300">
                              {skill}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedUser.skills?.technical && selectedUser.skills.technical.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-purple-700">Technical Skills</p>
                        <div className="flex flex-wrap gap-2">
                          {selectedUser.skills.technical.map((skill, index) => (
                            <Badge key={index} variant="secondary" className="bg-indigo-100 text-indigo-800">
                              {skill}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedUser.skills?.soft && selectedUser.skills.soft.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-purple-700">Soft Skills</p>
                        <div className="flex flex-wrap gap-2">
                          {selectedUser.skills.soft.map((skill, index) => (
                            <Badge key={index} variant="outline" className="bg-pink-50 text-pink-700 border-pink-300">
                              {skill}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedUser.skills?.certifications && selectedUser.skills.certifications.length > 0 && (
                      <div className="space-y-2 lg:col-span-2">
                        <p className="text-sm font-medium text-purple-700">Certifications</p>
                        <div className="flex flex-wrap gap-2">
                          {selectedUser.skills.certifications.map((cert, index) => (
                            <Badge key={index} variant="secondary" className="bg-amber-100 text-amber-800">
                              {cert}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Education */}
              {selectedUser.education && (
                <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-orange-900 mb-4 flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    Education
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-primary">Degree</p>
                      <p className="text-orange-900">{selectedUser.education.degree || 'Not specified'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-primary">Institution</p>
                      <p className="text-orange-900">{selectedUser.education.institution || 'Not specified'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-primary">Field of Study</p>
                      <p className="text-orange-900">{selectedUser.education.fieldOfStudy || 'Not specified'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-primary">Graduation Year</p>
                      <p className="text-orange-900">{selectedUser.education.graduationYear || 'Not specified'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-primary">GPA</p>
                      <p className="text-orange-900">{selectedUser.education.gpa || 'Not specified'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Professional Experience */}
              {selectedUser.experience && selectedUser.experience.length > 0 && (
                <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-secondary mb-4 flex items-center gap-2">
                    <Briefcase className="h-5 w-5" />
                    Professional Experience
                  </h3>
                  <div className="space-y-4">
                    {selectedUser.experience.map((exp, index) => (
                      <div key={index} className="bg-surface p-4 rounded-lg border border-border">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                          <div>
                            <p className="font-semibold text-secondary">{exp.position}</p>
                            <p className="text-gray-700">{exp.company}</p>
                          </div>
                          <div className="text-sm text-gray-600">
                            <p>{exp.startDate} - {exp.isCurrent ? 'Present' : exp.endDate}</p>
                            {exp.isCurrent && (
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300 mt-1">
                                Current Position
                              </Badge>
                            )}
                          </div>
                        </div>
                        {exp.description && (
                          <p className="text-gray-600 text-sm">{exp.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Career Goals */}
              {selectedUser.careerGoals && (
                <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-teal-900 mb-4 flex items-center gap-2">
                    <Trophy className="h-5 w-5" />
                    Career Goals
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-teal-700">Dream Company</p>
                      <p className="text-teal-900">{selectedUser.careerGoals.dreamCompany || 'Not specified'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-teal-700">Target Role</p>
                      <p className="text-teal-900">{selectedUser.careerGoals.targetRole || 'Not specified'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-teal-700">Salary Expectations</p>
                      <p className="text-teal-900">{selectedUser.careerGoals.salaryExpectations || 'Not specified'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-teal-700">Timeline</p>
                      <p className="text-teal-900">{selectedUser.careerGoals.timeline || 'Not specified'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Professional Info (for mentors) */}
              {selectedUser.professionalInfo && (
                <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-emerald-900 mb-4 flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    Professional Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-emerald-700">Current Company</p>
                      <p className="text-emerald-900">{selectedUser.professionalInfo.currentCompany || 'Not specified'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-emerald-700">Current Position</p>
                      <p className="text-emerald-900">{selectedUser.professionalInfo.currentPosition || 'Not specified'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-emerald-700">Years of Experience</p>
                      <p className="text-emerald-900">{selectedUser.professionalInfo.yearsOfExperience || 'Not specified'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-emerald-700">Industry</p>
                      <p className="text-emerald-900">{selectedUser.professionalInfo.industry || 'Not specified'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-emerald-700">Department</p>
                      <p className="text-emerald-900">{selectedUser.professionalInfo.department || 'Not specified'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Achievements */}
              {selectedUser.achievements && selectedUser.achievements.length > 0 && (
                <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-yellow-900 mb-4 flex items-center gap-2">
                    <Trophy className="h-5 w-5" />
                    Achievements
                  </h3>
                  <div className="space-y-2">
                    {selectedUser.achievements.map((achievement, index) => (
                      <div key={index} className="flex items-start gap-3 bg-surface p-3 rounded-lg border border-yellow-200">
                        <div className="w-2 h-2 bg-primary-light0 rounded-full mt-2 flex-shrink-0"></div>
                        <p className="text-yellow-900">{achievement}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="border-t border-slate-100 pt-4">
            <Button 
              variant="outline" 
              onClick={() => setShowFullProfile(false)}
              className="bg-surface/80 border-slate-300 hover:bg-surface"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Job Details Dialog */}
      <Dialog open={showJobDetails} onOpenChange={setShowJobDetails}>
        <DialogContent className="max-w-4xl bg-surface/95 backdrop-blur-sm border-slate-200">
          <DialogHeader className="border-b border-slate-100 pb-4">
            <DialogTitle className="text-xl font-semibold text-slate-900">Job Details</DialogTitle>
            <DialogDescription className="text-slate-600">
              Detailed information about the selected job posting
            </DialogDescription>
          </DialogHeader>
          {selectedJob && (
            <div className="space-y-6 py-4">
              <div className="flex items-start space-x-4 pb-4 border-b border-slate-100">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                  <Briefcase className="h-8 w-8 text-white" />
                </div>
                <div className="flex-1 space-y-2">
                  <h3 className="text-lg font-semibold text-slate-900">{selectedJob.title}</h3>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                      {selectedJob.companyName}
                    </Badge>
                    <Badge className={`${
                      selectedJob.status === 'approved' ? 'bg-emerald-100 text-emerald-800' :
                      selectedJob.status === 'rejected' ? 'bg-red-100 text-red-800' :
                      'bg-amber-100 text-amber-800'
                    }`}>
                      {selectedJob.status}
                    </Badge>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Job Title</Label>
                    <p className="text-sm text-slate-900 mt-1">{selectedJob.title}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Company</Label>
                    <p className="text-sm text-slate-900 mt-1">{selectedJob.companyName}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Location</Label>
                    <p className="text-sm text-slate-900 mt-1">{selectedJob.location}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Employment Type</Label>
                    <p className="text-sm text-slate-900 mt-1">{selectedJob.employmentType}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Experience Level</Label>
                    <p className="text-sm text-slate-900 mt-1">{selectedJob.experienceLevel}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Contact Email</Label>
                    <p className="text-sm text-slate-900 mt-1">{selectedJob.contactEmail || 'Not provided'}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Contact Phone</Label>
                    <p className="text-sm text-slate-900 mt-1">{selectedJob.contactPhone || 'Not provided'}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Salary Range</Label>
                    <p className="text-sm text-slate-900 mt-1">
                      {selectedJob.salaryRange?.min && selectedJob.salaryRange?.max 
                        ? `₹${selectedJob.salaryRange.min.toLocaleString()} - ₹${selectedJob.salaryRange.max.toLocaleString()}`
                        : 'Not specified'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Posted By</Label>
                    <p className="text-sm text-slate-900 mt-1">
                      {selectedJob.postedBy.firstName} {selectedJob.postedBy.lastName}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Posted Date</Label>
                    <p className="text-sm text-slate-900 mt-1">
                      {new Date(selectedJob.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
              </div>

              {selectedJob.description && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Job Description</Label>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-900 whitespace-pre-wrap">{selectedJob.description}</p>
                  </div>
                </div>
              )}

              {selectedJob.requiredSkills && selectedJob.requiredSkills.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Required Skills</Label>
                  <div className="flex flex-wrap gap-2">
                    {selectedJob.requiredSkills.map((skill, index) => (
                      <Badge key={index} variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Job Dialog */}
      <Dialog open={showJobRejectDialog} onOpenChange={setShowJobRejectDialog}>
        <DialogContent className="max-w-md bg-surface/95 backdrop-blur-sm border-slate-200">
          <DialogHeader className="border-b border-slate-100 pb-4">
            <DialogTitle className="text-xl font-semibold text-slate-900 flex items-center gap-2">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              Reject Job
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              Provide a reason for rejecting this job posting
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {selectedJob && (
              <div className="flex items-center space-x-3 p-4 bg-slate-50 rounded-lg">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                  <Briefcase className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">{selectedJob.title}</p>
                  <p className="text-sm text-slate-500">{selectedJob.companyName}</p>
                </div>
              </div>
            )}
            <div className="space-y-3">
              <Label htmlFor="job-rejection-reason" className="text-sm font-medium text-slate-700">
                Rejection Reason
              </Label>
              <Textarea
                id="job-rejection-reason"
                placeholder="Please provide a detailed reason for rejection..."
                value={jobRejectionReason}
                onChange={(e) => setJobRejectionReason(e.target.value)}
                rows={4}
                className="bg-surface/80 border-slate-200 focus:border-red-500 focus:ring-red-500 resize-none"
              />
              <p className="text-xs text-slate-500">
                Minimum 10 characters required
              </p>
            </div>
          </div>
          <DialogFooter className="border-t border-slate-100 pt-4">
            <Button 
              variant="outline" 
              onClick={() => setShowJobRejectDialog(false)}
              className="bg-surface/80 border-slate-300 hover:bg-surface"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => selectedJob && rejectJob(selectedJob._id, jobRejectionReason)}
              disabled={!jobRejectionReason.trim() || jobRejectionReason.length < 10}
              className="bg-red-600 hover:bg-red-700"
            >
              <XCircle className="h-4 w-4 mr-2" />
              Reject Job
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ticket Details Dialog */}
      <Dialog open={showTicketDetails} onOpenChange={setShowTicketDetails}>
        <DialogContent className="max-w-2xl bg-surface/95 backdrop-blur-sm border-slate-200">
          <DialogHeader className="border-b border-slate-100 pb-4">
            <DialogTitle className="text-xl font-semibold text-slate-900">Ticket Details</DialogTitle>
            <DialogDescription className="text-slate-600">
              Detailed information about the selected support ticket
            </DialogDescription>
          </DialogHeader>
          {selectedTicket && (
            <div className="space-y-6 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Ticket ID</Label>
                  <p className="text-slate-900 font-mono">#{selectedTicket.bugId}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Reporter</Label>
                  <p className="text-slate-900">{selectedTicket.reporterName}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Status</Label>
                  <Badge className={getStatusColor(selectedTicket.status)}>
                    {selectedTicket.status}
                  </Badge>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Priority</Label>
                  <Badge className={getPriorityColor(selectedTicket.priority)}>
                    {selectedTicket.priority}
                  </Badge>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Module</Label>
                  <p className="text-slate-900">{selectedTicket.module}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Type</Label>
                  <p className="text-slate-900">{selectedTicket.bugType}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Created</Label>
                  <p className="text-slate-900">
                    {new Date(selectedTicket.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700">Issue Description</Label>
                <p className="text-slate-900 mt-2 bg-slate-50 p-3 rounded-lg">
                  {selectedTicket.issueDescription}
                </p>
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700">Steps to Reproduce</Label>
                <p className="text-slate-900 mt-2 bg-slate-50 p-3 rounded-lg">
                  {selectedTicket.stepsToReproduce}
                </p>
              </div>

              {selectedTicket.adminNotes && (
                <div>
                  <Label className="text-sm font-medium text-slate-700">Admin Notes</Label>
                  <p className="text-slate-900 mt-2 bg-amber-50 p-3 rounded-lg">
                    {selectedTicket.adminNotes}
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Ticket Dialog */}
      <Dialog open={showDeleteTicketDialog} onOpenChange={setShowDeleteTicketDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Ticket</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this ticket? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteTicketDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDeleteTicket}>
              Delete Ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={showDeleteUserDialog} onOpenChange={setShowDeleteUserDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete this user? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteUserDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDeleteUser}>
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Job Dialog */}
      <Dialog open={showDeleteJobDialog} onOpenChange={setShowDeleteJobDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Job</DialogTitle>
            <DialogDescription>
              {jobToDelete?.status === 'approved' ? (
                <div className="space-y-2">
                  <p className="font-semibold text-red-600">⚠️ WARNING: This job is APPROVED and LIVE on the platform!</p>
                  <p>Deleting it will:</p>
                  <ul className="list-disc ml-5 space-y-1">
                    <li>Remove it from all job listings</li>
                    <li>Make it unavailable to job seekers</li>
                    <li>Potentially affect users who bookmarked it</li>
                  </ul>
                  <p>Are you sure you want to proceed?</p>
                </div>
              ) : (
                'Are you sure you want to delete this job? This action cannot be undone.'
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteJobDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDeleteJob}>
              Delete Job
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Confirmation Dialog */}
      <Dialog open={!!showPaymentDialog} onOpenChange={() => setShowPaymentDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {showPaymentDialog?.action === 'approve' ? 'Mark as Paid' : 'Revert Payment'}
            </DialogTitle>
            <DialogDescription>
              {showPaymentDialog?.action === 'approve' ? (
                <div className="space-y-2">
                  <p>Are you sure you want to mark <strong>{showPaymentDialog?.userName}</strong> as paid for <strong>{showPaymentDialog?.hackathonTitle}</strong>?</p>
                  <p>This will:</p>
                  <ul className="list-disc ml-5 space-y-1">
                    <li>Create a payment record of ₹250</li>
                    <li>Register the user for the hackathon</li>
                    <li>Send a payment receipt email to the user</li>
                    <li>Set registration status to 'confirmed'</li>
                  </ul>
                </div>
              ) : (
                <div className="space-y-2">
                  <p>Are you sure you want to revert the payment for <strong>{showPaymentDialog?.userName}</strong> for <strong>{showPaymentDialog?.hackathonTitle}</strong>?</p>
                  <p>This will:</p>
                  <ul className="list-disc ml-5 space-y-1">
                    <li>Mark the payment as 'refunded'</li>
                    <li>Set registration status to 'withdrawn'</li>
                    <li>Remove the user from the hackathon</li>
                  </ul>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(null)}>
              Cancel
            </Button>
            <Button 
              variant={showPaymentDialog?.action === 'approve' ? 'default' : 'destructive'}
              onClick={confirmPaymentAction}
              disabled={!!processingPayment}
            >
              {processingPayment ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  {showPaymentDialog?.action === 'approve' ? 'Mark as Paid' : 'Revert Payment'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
} 